import { useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useUnleashClient } from '@unleash/proxy-client-react';
import { useMachine } from '@xstate/react';
import { format, parseISO } from 'date-fns';
import { showErrorToast } from 'libs/shared/ui/src/components/ToastUtils/ToastUtils';
import { MutationStatus } from 'react-query';
import { assign, spawn } from 'xstate';

import { useAuthCoreMachine } from '@nw/auth-core';
import {
  formSubmissionActionType,
  generateCommonRequestProperties
} from '@nw/fields-manager';
import {
  Config,
  deviceDetailsConstants,
  LogEntriesConstantsEnums,
  LogEntryPriorityEnum,
  LogEntryTypesEnums,
  utmParameters
} from '@nw/shared/config';
import {
  GetFormReferralCodeResponseType,
  getNiatApplicationDynamicMutation,
  getNiatApplicationDynamicQuery,
  getUserAddressDetailQuery,
  getUserGuardianDetailsGraphQlQuery,
  getUserIntermediateDetailsGraphQlQuery,
  getUserInviteCodeGraphQlQuery,
  getUserProfessionalDetailQuery,
  getUserProfileDetailGraphQlQuery,
  getUserSSCDetailsGraphQlQuery,
  IdentityEnums,
  setAccessTokenToDomain,
  setRefreshTokenToDomain,
  setUserId,
  UpdateMultipleTemplateResponseRequestObjectType,
  UpdateTemplateResponseRequestObjectType,
  useCreateIbFormSubmission,
  useCreateUserApplicationApi,
  useCreateUserProgramInstituteApi,
  useFormReferralTrack,
  useGetApplicationDetailsApi,
  useGetApplicationDiscountDetailsApi,
  useGetApplicationSectionsDetails,
  useGetClientAuthCodeAPI,
  useGetFormReferralCode,
  useGetIbFormDetails,
  useGetIbFormSubmissionDetails,
  useGetOrCreateApplicationSectionVisitApi,
  useGetProgramInstituteCoursesApi,
  useGetTemplateResponse,
  useGetUserIdentityAccessTokenApi,
  useGetUserLogoutAPI,
  useGetUserOrdersDetailsApi,
  useGetUserProfileDetailGraphQL,
  useGetUserProgramInstituteCoursesApi,
  useIbUserAccountsDynamicMutationAPI,
  useIbUserAccountsDynamicQueryAPI,
  useLinkUserApplicationApi,
  useNwProductsExtensionsService,
  useRecordDiscountInteractionApi,
  useSendUserEventApi,
  useUpdateCouponExpiryDatetimeApi,
  useUpdateMultipleTemplateResponse,
  useUpdateTemplateResponse,
  useUpdateUserApplicationApi,
  useUpdateUserExtraInfoDetails,
  useUpdateUserGuardianDetailsApi,
  useUpdateUserIntermediateDetailGraphQl,
  useUpdateUserProfileDetailsGraphQl,
  useUpdateUserSSCDetailsGraphQl
} from '@nw/shared/data-access';
import {
  captureLogEntriesExceptionWithScope,
  ERROR,
  getAPIErrorEnum,
  getAPIErrorResponse,
  getDeviceDetailsObjectBasedOnKeys,
  getQueryParamValue,
  getSelectedQueryParamsObject,
  IDLE,
  INVITE_CODE,
  LOADING,
  loadSegmentScript,
  sanitizeEmojisFromPayload,
  sendGAEventToDataLayer,
  sendSegmentIdentifyEventWithBackendResponse,
  SUCCESS
} from '@nw/shared/utils';

import { ApplicationSectionNameEnums } from '../../constants/ApplicationSectionConstants';
import { NIAT_APPLICATION_CLIENT_ID } from '../../constants/ClientBasedAuthConfig';
import {
  CloudWatchEventNames,
  CloudWatchEventTypes
} from '../../constants/CloudWatchEventConstants';
import {
  niatApplicationMachineActions,
  niatApplicationMachineGuards,
  niatApplicationMachineServices
} from '../../constants/niatApplicationMachineConstants';
import { NiatApplicationMachine } from '../../machines/NiatApplicationMachine';
import ApplicationSectionFormModel from '../../stores/models/ApplicationSectionFormModel';
import ApplicationSectionsDetailModel from '../../stores/models/ApplicationSectionsDetailModel';
import { DiscountDetailsModel } from '../../stores/models/DiscountDetailsModel';
import { UserApplicationDetailModel } from '../../stores/models/UserApplicationDetaillsModel';
import { sectionDetailsConfigTransformer } from '../../stores/transformers/sectionDetailsConfigTransformer';
import {
  NiatApplicationMachineContextType,
  NiatApplicationMachineSendType,
  NiatApplicationMachineStateType,
  UpdateFieldValues,
  UpdateSeatReservationInstituteSectionDetailsEvent,
  UpdateUserProfileDetailGraphQl
} from '../../types/niatApplicationMachineTypes';
import {
  getSectionDetailsDisableInfoTabsConfig,
  getSectionDetailsInfoTabsConfig,
  isDirectCampusVisitedApplication,
  isOfflineExamSubmissionApplication,
  isSectionCompleted,
  OFFLINE_EXAM_SUBMISSION_APPLICATION_TYPE_FIELD_ID
} from '../../utils/ApplicationSectionUtils';
import { getLatestUserProgramInstituteCourseRow } from '../../utils/EnrollmentFeeUtils';
import { sendUnleashImpressionGaEvent } from '../../utils/sendUnleashImpressionGaEvent';
import { getTimerExpiryState } from '../../utils/TimeUtils';

import { useCloudWatchLogger } from '../useCloudWatchLogger';
import { useProductsMachine } from '../useProductsMachine';

export type NiatApplicationMachineReturnType = {
  niatApplicationMachineState: NiatApplicationMachineStateType;
  niatApplicationMachineSend: NiatApplicationMachineSendType;
  apiStatus: {
    getApplicationDetailAPIStatus: MutationStatus;
    getApplicationSectionsDetailsAPIStatus: MutationStatus;
    createIbFormSubmissionAPIStatus: MutationStatus;
  };
  apiErrors: {};
};

const services = niatApplicationMachineServices;
const actions = niatApplicationMachineActions;
const guards = niatApplicationMachineGuards;
const NIAT_PROGRAM_ID = Number(Config['NIAT_PROGRAM_ID']);

export const useNiatApplicationMachine =
  (): NiatApplicationMachineReturnType => {
    const { userLogoutAPI } = useGetUserLogoutAPI();

    const { getApplicationDetailsApi, status: getApplicationDetailAPIStatus } =
      useGetApplicationDetailsApi();

    const {
      getApplicationSectionsDetailsApi,
      status: getApplicationSectionsDetailsAPIStatus
    } = useGetApplicationSectionsDetails();

    const { getUserProfileDetailGraphQLApi } = useGetUserProfileDetailGraphQL();

    const { createUserApplicationApi } = useCreateUserApplicationApi();

    const { updateUserApplicationApi } = useUpdateUserApplicationApi();

    const { updateUserProfileDetailsGraphQLApi } =
      useUpdateUserProfileDetailsGraphQl();

    const { updateUserIntermediateDetailGraphQLApi } =
      useUpdateUserIntermediateDetailGraphQl();

    const { getMultipleGraphQlQueriesResponse } =
      useIbUserAccountsDynamicQueryAPI();

    const { getIbFormSubmissionDetailsAPI } = useGetIbFormSubmissionDetails();

    const { linkUserApplicationApi } = useLinkUserApplicationApi();

    const { updateUserGuardianDetailsGraphQLApi } =
      useUpdateUserGuardianDetailsApi();

    const { updateUserSSCDetailsGraphQLApi } = useUpdateUserSSCDetailsGraphQl();

    const { getUserIdentityAccessTokenApi } =
      useGetUserIdentityAccessTokenApi();

    const { getAuthCoreMachineWithConfig } = useAuthCoreMachine();

    const { getProductsMachineConfig } = useProductsMachine();
    const { getClientAuthCodeAPI } = useGetClientAuthCodeAPI();
    const { nwProductsExtensionsServiceInstance } =
      useNwProductsExtensionsService();

    const {
      logEvent,
      trackSectionEntry,
      trackSectionExit,
      trackSessionEnd,
      flush
    } = useCloudWatchLogger();

    const lastOnEntryGetOtpLogTimeRef = useRef<number | null>(null);

    const {
      createIbFormSubmissionAPI,
      status: createIbFormSubmissionAPIStatus
    } = useCreateIbFormSubmission();

    const { getIbFormDetailsAPI } = useGetIbFormDetails();

    const { updateTemplateResponseApi } = useUpdateTemplateResponse();

    const { updateMultipleTemplateResponseApi } =
      useUpdateMultipleTemplateResponse();
    const { updateUserExtraInfoApi } = useUpdateUserExtraInfoDetails();

    const { getTemplateResponseApi } = useGetTemplateResponse();

    const { updateMultipleGraphQlMutations } =
      useIbUserAccountsDynamicMutationAPI();

    const { sendUserEventApi } = useSendUserEventApi();

    const { getApplicationDiscountDetailsApi } =
      useGetApplicationDiscountDetailsApi();

    const { recordDiscountInteractionApi } = useRecordDiscountInteractionApi();

    const { getFormReferralCodeApi } = useGetFormReferralCode();

    const { formReferralTrackApi } = useFormReferralTrack();
    const { getOrCreateApplicationSectionVisitApi } =
      useGetOrCreateApplicationSectionVisitApi();

    const { createUserProgramInstituteApi } =
      useCreateUserProgramInstituteApi();
    const { getUserProgramInstituteCoursesApi } =
      useGetUserProgramInstituteCoursesApi();
    const { getUserOrdersDetailsApi } = useGetUserOrdersDetailsApi();
    const { getProgramInstituteCourses } = useGetProgramInstituteCoursesApi();
    const { updateCouponExpiryDatetimeApi } =
      useUpdateCouponExpiryDatetimeApi();
    const unleashClient = useUnleashClient();

    const {
      deviceType,
      deviceBrowserName,
      deviceOSName,
      deviceOSVersion,
      deviceBrowserVersion
    } = deviceDetailsConstants;

    const device_details = getDeviceDetailsObjectBasedOnKeys({
      deviceType,
      deviceBrowserName,
      deviceBrowserVersion,
      deviceOSName,
      deviceOSVersion
    });

    const graphQlSingleFieldUpdate = async (
      context: NiatApplicationMachineContextType,
      fieldDetail: {
        field: string;
        value: string;
        extraDetailToUpdate?: Record<string, any>;
      }
    ) => {
      const { field, value, extraDetailToUpdate = {} } = fieldDetail;
      const fieldGraphQlConfig = context.fieldsGraphQlConfig[field];

      if (!fieldGraphQlConfig) {
        return {};
      }

      const modelName = fieldGraphQlConfig.modelName;
      const fieldName = fieldGraphQlConfig.fieldName;
      const sectionId = fieldGraphQlConfig.sectionId;

      let valuesToUpdate: Record<string, any> = {
        [fieldName]: value,
        section_id: sectionId
      };

      if (valuesToUpdate?.state_name || valuesToUpdate?.district_name) {
        valuesToUpdate = {
          ...valuesToUpdate,
          ...extraDetailToUpdate
        };
      }

      if (modelName === 'UserProfile') {
        return updateUserProfileDetailsGraphQLApi(
          sanitizeEmojisFromPayload(valuesToUpdate)
        );
      }
      if (modelName === 'UserIntermediateDetails') {
        if (fieldName === 'score_type') {
          valuesToUpdate = {
            ...valuesToUpdate,
            score: 'NULL',
            score_in_maths: 'NULL',
            first_year_score: 'NULL'
          };
        }
        if (fieldName === 'year_of_completion') {
          valuesToUpdate = {
            ...valuesToUpdate,
            year_of_completion: Number(valuesToUpdate.year_of_completion)
          };
        }
        return updateUserIntermediateDetailGraphQLApi(
          sanitizeEmojisFromPayload(valuesToUpdate)
        );
      }
      if (modelName === 'UserGuardianDetails') {
        return updateUserGuardianDetailsGraphQLApi({
          values: sanitizeEmojisFromPayload(valuesToUpdate)
        });
      }
      if (modelName === 'UserSSCDetails') {
        if (fieldName === 'score_type') {
          valuesToUpdate = {
            ...valuesToUpdate,
            score: 'NULL',
            score_in_maths: 'NULL'
          };
        }
        if (fieldName === 'year_of_completion') {
          valuesToUpdate = {
            ...valuesToUpdate,
            year_of_completion: Number(valuesToUpdate.year_of_completion)
          };
        }
        return updateUserSSCDetailsGraphQLApi(
          sanitizeEmojisFromPayload(valuesToUpdate)
        );
      }
      if (modelName === 'UserAddressDetails') {
        const requestObjectForMutation = {
          UserAddressDetails: {
            ...valuesToUpdate
          }
        };

        const { mutation, data } = getNiatApplicationDynamicMutation(
          'UpdateUserAddressDetails',
          sanitizeEmojisFromPayload(requestObjectForMutation)
        );

        return updateMultipleGraphQlMutations({
          mutation: mutation,
          data: sanitizeEmojisFromPayload(data)
        });
      }
      if (modelName === 'UserCurrentProfessionalDetails') {
        const requestObjectForMutation = {
          UserCurrentProfessionalDetails: {
            ...valuesToUpdate
          }
        };
        const { mutation, data } = getNiatApplicationDynamicMutation(
          'UserCurrentProfessionalDetails',
          sanitizeEmojisFromPayload(requestObjectForMutation)
        );

        return updateMultipleGraphQlMutations({
          mutation: mutation,
          data: sanitizeEmojisFromPayload(data)
        });
      }
      return {};
    };

    const niatApplicationMachineConfig = NiatApplicationMachine.withConfig({
      actions: {
        [actions.onSuccessGetGraphQLProfileDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const response = event?.data ?? {};

            const { user_profile_details, user_invite_code } = response;

            if (!user_profile_details?.success_response) {
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  getUserProfileAndIntermediateDetailGraphQlApiStatus: ERROR
                }
              };
            }

            const userProfileDetail = user_profile_details.success_response;
            setUserId(userProfileDetail.user_id);
            loadSegmentScript();
            sendSegmentIdentifyEventWithBackendResponse(userProfileDetail);

            const apiInviteCode =
              user_invite_code?.success_response?.invite_code || '';

            return {
              userGraphQlProfileDetails: userProfileDetail,
              userApiInviteCode: apiInviteCode,
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getUserProfileAndIntermediateDetailGraphQlApiStatus: SUCCESS
              }
            };
          }
        ),
        [actions.onFailGetGraphQLProfileDetails]: assign(
          (context: NiatApplicationMachineContextType, event) => {
            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getUserProfileAndIntermediateDetailGraphQlApiStatus: ERROR
              }
            };
          }
        ),
        [actions.assignErrorMessageToContext]: assign(({ context, event }) => {
          const errorMessage = getAPIErrorResponse(event?.data);
          return {
            errorMessage: errorMessage
          };
        }) as any,
        [actions.onSuccessGettingUserApplicationDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const userApplicationDetail = new UserApplicationDetailModel(
              event.data,
              context.userGraphQlProfileDetails['user_id']
            );

            unleashClient.updateContext({
              userId: context.userGraphQlProfileDetails['user_id'],
              properties: {
                applicationType:
                  context.initialValues?.APPLICATION_TYPE ||
                  userApplicationDetail.applicationType
              }
            });

            return {
              userApplicationDetails: userApplicationDetail
            };
          }
        ),
        [actions.spawnFieldSpecificAuthMachine]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { fieldDetails } = event as any;

            const { fieldType, fieldName, fieldValue } = fieldDetails;

            return {
              fieldsAuthMachineRefDetails: {
                ...context.fieldsAuthMachineRefDetails,
                [fieldName]: spawn(
                  getAuthCoreMachineWithConfig(
                    fieldValue,
                    NIAT_APPLICATION_CLIENT_ID,
                    fieldType,
                    false,
                    fieldName
                  ),
                  { sync: true }
                )
              }
            };
          }
        ),
        [actions.updateValueOnContext]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              values: {
                ...context.values,
                ...event?.values
              }
            };
          }
        ),
        [actions.setEnrollmentCourseIdForPayment]: assign(
          (_context: NiatApplicationMachineContextType, event: any) => ({
            enrollmentCourseIdForPayment: String(
              event?.enrollmentCourseId ?? ''
            ).trim()
          })
        ),
        [actions.assignSeatReservationFlowToContext]: assign(
          (_context: NiatApplicationMachineContextType, event: any) => {
            return {
              seatReservationFlow: event?.seatReservationFlow ?? null
            };
          }
        ),
        [actions.updateUserGraphQlProfileDetailsInContext]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { values } = event as UpdateUserProfileDetailGraphQl;
            const response = event.data;
            const modelKey = Object.keys(response)[0];
            const failureResponse = response[modelKey].failure_response;
            if (!failureResponse) {
              return {
                userGraphQlProfileDetails: {
                  ...context.userGraphQlProfileDetails,
                  ...values
                }
              };
            } else {
              const resStatus = failureResponse.res_status;
              if (resStatus === 'INVALID_USER_PROFILE_DETAILS') {
                const responseStr = failureResponse.response
                  .replace(/'/g, '"')
                  .replace(/None/g, 'null');
                const parsedResponse = JSON.parse(responseStr);
                const errorMessages = {};

                if (parsedResponse.profile_details) {
                  Object.entries(parsedResponse.profile_details).forEach(
                    ([key, value]) => {
                      if (
                        value &&
                        typeof value === 'object' &&
                        value.error_message
                      ) {
                        errorMessages[key] = value.error_message;
                      }
                    }
                  );
                }

                return {
                  updateFieldFailureErrorMessages: {
                    ...context.updateFieldFailureErrorMessages,
                    ...{
                      profile_details: errorMessages
                    }
                  }
                };
              }
              captureLogEntriesExceptionWithScope(
                LogEntriesConstantsEnums.GRAPHQL_FIELD_UPDATE_FAILURE,
                {
                  data: event.data
                },
                LogEntryTypesEnums.ERROR,
                LogEntryPriorityEnum.HIGH
              );

              return {};
            }
          }
        ),
        [actions.onSuccessCreateUserApplication]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { values, userGraphQlProfileDetails, initialValues } =
              context;
            const userApplicationDetail = new UserApplicationDetailModel(
              event.data,
              userGraphQlProfileDetails['user_id']
            );

            unleashClient.updateContext({
              userId: context.userGraphQlProfileDetails['user_id'],
              properties: {
                applicationType:
                  initialValues?.APPLICATION_TYPE ||
                  userApplicationDetail.applicationType
              }
            });

            return {
              userApplicationDetails: userApplicationDetail
            };
          }
        ),

        [actions.onSuccessUpdateUserApplication]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const {
              initialValues,
              sectionDetailsInfoTabsConfig,
              currentSelectedSubSectionId,
              currentSelectedSectionTabId,
              loggedInUserRole,
              values
            } = context;
            const loggedInUser =
              initialValues['WHO_IS_FILLING'] ??
              values['WHO_IS_FILLING'] ??
              loggedInUserRole;

            const { sectionDetailsInfoTabsDetailsArray: sectionDetailsConfig } =
              getSectionDetailsDisableInfoTabsConfig(
                sectionDetailsInfoTabsConfig,
                loggedInUser,
                undefined,
                context.userApplicationDetails?.applicationType,
                initialValues?.APPLICATION_TYPE,
                isDirectCampusVisitedApplication(initialValues)
              );
            const idToBeUpdate =
              currentSelectedSubSectionId || currentSelectedSectionTabId;

            if (!idToBeUpdate) {
              Sentry.addBreadcrumb({
                type: 'info',
                category: 'actions.onSuccessUpdateUserApplication',
                message: `idToBeUpdate is undefined`,
                data: {
                  idToBeUpdate
                }
              });
            }

            return {
              loggedInUserRole: loggedInUser,
              sectionDetailsInfoTabsConfig: sectionDetailsConfig
            };
          }
        ),
        [actions.goToApplicationSectionPage]: () => {
          window.location.reload();
        },
        [actions.onSuccessLinkStudentToParent]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              isUserApplicationNotExit: false
            };
          }
        ),
        [actions.setIsGetOtpButtonLoadingToTrue]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isGetOtpButtonLoading: true
            };
          }
        ),
        [actions.setIsGetOtpButtonLoadingToFalse]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isGetOtpButtonLoading: false
            };
          }
        ),
        [actions.setIsGetEmailOtpButtonLoadingToTrue]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isGetEmailOtpButtonLoading: true
            };
          }
        ),
        [actions.setIsGetEmailOtpButtonLoadingToFalse]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isGetEmailOtpButtonLoading: false
            };
          }
        ),
        [actions.setIsProceedButtonLoadingToTrue]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isProceedButtonLoading: true
            };
          }
        ),
        [actions.setIsProceedButtonLoadingToFalse]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              isProceedButtonLoading: false
            };
          }
        ),
        [actions.clearErrorMessageFromContext]: assign(
          (context: NiatApplicationMachineContextType, event) => {
            return {
              errorMessages: {
                referralCodeErrorMessage: '',
                createPaymentOrderAndGetDetailsErrorMessage: ''
              }
            };
          }
        ),
        [actions.updateLoggedInUserRole]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const role = event?.role;
            const { userApplicationDetails } = context;

            const loggedInUser =
              userApplicationDetails.isStudentApplicationExist
                ? 'STUDENT'
                : userApplicationDetails.isParentApplicationExist
                  ? 'PARENT'
                  : '';

            return {
              loggedInUserRole: role ?? loggedInUser
            };
          }
        ),
        [actions.onSuccessGettingUserApplicationSectionsDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { loggedInUserRole, userGraphQlProfileDetails } = context;

            const userId = userGraphQlProfileDetails?.user_id || '';

            const {
              allSections: formattedSectionDetails,
              fieldsGraphQlConfig,
              fieldsYupValidationConfig,
              fieldsTemplateResponseConfig,
              templatesToGetInitialValues
            } = sectionDetailsConfigTransformer(event.data, loggedInUserRole);

            const applicationSectionDetail = new ApplicationSectionsDetailModel(
              formattedSectionDetails,
              fieldsYupValidationConfig
            );

            const {
              sectionDetailsInfoTabsDetailsArray: sectionDetailsInfoTabsConfig,
              selectedSectionTabId,
              selectedSubSectionId,
              selectedSectionCompletionPercentage
            } = getSectionDetailsInfoTabsConfig(
              formattedSectionDetails,
              loggedInUserRole,
              context.userApplicationDetails?.applicationType,
              context.initialValues?.APPLICATION_TYPE,
              isDirectCampusVisitedApplication(context.initialValues)
            );

            if (
              selectedSectionTabId ===
              ApplicationSectionNameEnums.PERSONAL_DETAILS
            ) {
              sendGAEventToDataLayer({
                event: 'personal_details_page_visit'
              });
              sendUnleashImpressionGaEvent(
                unleashClient,
                'personal-details-cta'
              );
              sendUnleashImpressionGaEvent(
                unleashClient,
                'personal-details-cta-confirmation-message'
              );
              logEvent(
                CloudWatchEventNames.PERSONAL_DETAILS_PAGE_VISIT,
                'page_visit',
                selectedSectionTabId,
                userId
              );
            } else if (
              selectedSectionTabId ===
              ApplicationSectionNameEnums.APPLICATION_FEE_PAYMENT
            ) {
              sendGAEventToDataLayer({
                event: 'payment_page_visit'
              });
              logEvent(
                CloudWatchEventNames.PAYMENT_PAGE_VISIT,
                'page_visit',
                selectedSectionTabId,
                userId
              );
            } else if (
              selectedSectionTabId ===
              ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
            ) {
              sendGAEventToDataLayer({
                event: 'book_admission_test_fee_page_visit'
              });
              logEvent(
                CloudWatchEventNames.BOOK_ADMISSION_TEST_FEE_PAGE_VISIT,
                'page_visit',
                selectedSectionTabId,
                userId
              );
              sendUnleashImpressionGaEvent(
                unleashClient,
                'application-fee-cta'
              );
              sendUnleashImpressionGaEvent(
                unleashClient,
                'application-fee-share-payment-link'
              );
              sendUnleashImpressionGaEvent(unleashClient, 'payment-page-faq');
            } else if (
              selectedSectionTabId ===
              ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
            ) {
              sendGAEventToDataLayer({
                event: 'exam_slot_selection_visit'
              });
              logEvent(
                CloudWatchEventNames.EXAM_SLOT_SELECTION_VISIT,
                'page_visit',
                selectedSectionTabId,
                userId
              );
            } else if (
              selectedSectionTabId ===
              ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER
            ) {
              sendGAEventToDataLayer({
                event: 'campus_visit_scheduler_section_visit'
              });
              logEvent(
                CloudWatchEventNames.CAMPUS_VISIT_SCHEDULER_SECTION_VISIT,
                'page_visit',
                selectedSectionTabId,
                userId
              );
            }

            const initialPageId = selectedSubSectionId || selectedSectionTabId;
            if (initialPageId && userId) {
              trackSectionEntry(
                selectedSectionTabId,
                selectedSubSectionId,
                userId
              );
            }

            return {
              applicationSectionDetailModel: applicationSectionDetail,
              sectionDetailsInfoTabsConfig: sectionDetailsInfoTabsConfig,
              fieldsGraphQlConfig: fieldsGraphQlConfig,
              currentSelectedSectionTabId: selectedSectionTabId,
              currentSelectedSectionCompletionPercentage:
                selectedSectionCompletionPercentage,
              currentSelectedSubSectionId: selectedSubSectionId,
              fieldsTemplateResponseConfig: fieldsTemplateResponseConfig,
              templatesToGetInitialValues: templatesToGetInitialValues
            };
          }
        ),
        [actions.setStudentLoginDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            if (event.data)
              return {
                studentLoginDetails: {
                  accessToken: event.data.accessToken,
                  refreshToken: event.data.refreshToken
                }
              };
            else return {};
          }
        ),

        [actions.setParentLoginDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              parentLoginDetails: {
                accessToken: event.data.accessToken,
                refreshToken: event.data.refreshToken
              }
            };
          }
        ),

        [actions.setCurrentAuthenticationField]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              currentAuthenticationField: event?.fieldDetails?.fieldName
            };
          }
        ),

        [actions.updateCurrentSelectedSectionTabId]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            let idToBeUpdate = event.tabId;

            if (!idToBeUpdate) {
              Sentry.addBreadcrumb({
                type: 'info',
                category: 'actions.updateCurrentSelectedSectionTabId',
                message: `idToBeUpdate is undefined`,
                data: {
                  idToBeUpdate,
                  event
                }
              });
            }

            const {
              currentSelectedSectionTabId,
              currentSelectedSubSectionId,
              sectionDetailsInfoTabsConfig,
              currentSelectedSectionCompletionPercentage,
              userGraphQlProfileDetails
            } = context;

            if (
              idToBeUpdate === currentSelectedSectionTabId ||
              idToBeUpdate === currentSelectedSubSectionId
            ) {
              return {};
            }

            const userId = userGraphQlProfileDetails?.user_id || '';
            const prevPageId =
              currentSelectedSubSectionId || currentSelectedSectionTabId;

            // Track exit from previous section
            if (prevPageId && userId) {
              trackSectionExit('navigation', prevPageId, userId);
            }

            let tabId;

            let subSectionTabId = '';

            let sectionPercentage = currentSelectedSectionCompletionPercentage;

            if (idToBeUpdate === ApplicationSectionNameEnums.PERSONAL_DETAILS) {
              sendGAEventToDataLayer({
                event: 'personal_details_page_visit'
              });
              sendUnleashImpressionGaEvent(
                unleashClient,
                'personal-details-cta'
              );
              sendUnleashImpressionGaEvent(
                unleashClient,
                'personal-details-cta-confirmation-message'
              );
              logEvent(
                CloudWatchEventNames.PERSONAL_DETAILS_PAGE_VISIT,
                'page_visit',
                idToBeUpdate,
                userId
              );
            } else if (
              idToBeUpdate ===
              ApplicationSectionNameEnums.APPLICATION_FEE_PAYMENT
            ) {
              sendGAEventToDataLayer({
                event: 'payment_page_visit'
              });
              logEvent(
                CloudWatchEventNames.PAYMENT_PAGE_VISIT,
                'page_visit',
                idToBeUpdate,
                userId
              );
            } else if (
              idToBeUpdate ===
              ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
            ) {
              sendGAEventToDataLayer({
                event: 'book_admission_test_fee_page_visit'
              });
              logEvent(
                CloudWatchEventNames.BOOK_ADMISSION_TEST_FEE_PAGE_VISIT,
                'page_visit',
                idToBeUpdate,
                userId
              );
            } else if (
              idToBeUpdate === ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
            ) {
              sendGAEventToDataLayer({
                event: 'exam_slot_selection_visit'
              });
              logEvent(
                CloudWatchEventNames.EXAM_SLOT_SELECTION_VISIT,
                'page_visit',
                idToBeUpdate,
                userId
              );
            } else if (
              idToBeUpdate ===
              ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER
            ) {
              sendGAEventToDataLayer({
                event: 'campus_visit_scheduler_section_visit'
              });
              logEvent(
                CloudWatchEventNames.CAMPUS_VISIT_SCHEDULER_SECTION_VISIT,
                'page_visit',
                idToBeUpdate,
                userId
              );
            }

            const getSubSectionIdBasedOnCompletion = (
              parentSectionId: string,
              subSections: any[]
            ): string => {
              if (
                parentSectionId ===
                ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
              ) {
                const values = context.values ?? {};
                const accommodationFieldName = Object.keys(values).find(
                  (fieldName) =>
                    fieldName === 'hostelTransportRequired' ||
                    fieldName.includes('ACCOMMODATION')
                );
                const accommodationValue = String(
                  values?.[accommodationFieldName ?? ''] ??
                    values?.hostelTransportRequired ??
                    ''
                ).toUpperCase();
                if (accommodationValue === 'NO') {
                  const registrationSubSection = subSections.find(
                    (subSection) =>
                      subSection.id ===
                      ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026
                  );
                  const isRegistrationCompleted =
                    (registrationSubSection?.completionPercentage ?? 0) >= 100;
                  return (
                    subSections.find(
                      (subSection) =>
                        subSection.id ===
                        (isRegistrationCompleted
                          ? ApplicationSectionNameEnums.NIAT_BOOTCAMP_FEE_2026
                          : ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026)
                    )?.id ??
                    subSections[0]?.id ??
                    ''
                  );
                }
              }

              const incompleteSubSection = subSections.find(
                (subSection) => subSection.completionPercentage < 100
              );

              if (incompleteSubSection) {
                return incompleteSubSection.id;
              }

              if (
                parentSectionId ===
                ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
              ) {
                return (
                  subSections.find(
                    (subSection) =>
                      subSection.id ===
                      ApplicationSectionNameEnums.NIAT_BOOTCAMP_FEE_2026
                  )?.id ??
                  subSections[0]?.id ??
                  ''
                );
              }

              return subSections[subSections.length - 1]?.id ?? '';
            };

            sectionDetailsInfoTabsConfig.forEach((eachSection) => {
              if (eachSection.id === idToBeUpdate) {
                tabId = eachSection.id;
                sectionPercentage = eachSection.completionPercentage;
                if (eachSection.subSection.length > 0) {
                  if (
                    eachSection.id ===
                      ApplicationSectionNameEnums.NAT_SLOT_SELECTION ||
                    eachSection.id ===
                      ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
                  ) {
                    subSectionTabId = getSubSectionIdBasedOnCompletion(
                      eachSection.id,
                      eachSection.subSection
                    );
                  } else {
                    subSectionTabId = eachSection.subSection[0].id;
                  }
                }
                return;
              } else {
                let isSubSectionHasTabId = false;
                eachSection.subSection.forEach((subSection) => {
                  if (subSection.id === idToBeUpdate) {
                    isSubSectionHasTabId = true;
                    sectionPercentage = subSection.completionPercentage;
                  }
                });
                if (isSubSectionHasTabId) {
                  tabId = eachSection.id;
                  subSectionTabId = idToBeUpdate;
                  return;
                }
              }
            });

            const newPageId = subSectionTabId || tabId;
            if (newPageId && userId) {
              trackSectionEntry(tabId, subSectionTabId, userId);
            }

            return {
              currentSelectedSectionTabId: tabId,
              currentSelectedSubSectionId: subSectionTabId,
              currentSelectedSectionCompletionPercentage: sectionPercentage
            };
          }
        ),

        [actions.navigateFromQualifiedPreview]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { sectionDetailsInfoTabsConfig } = context;
            const targetSectionId = event.tabId;

            const postNatSectionIds = [
              ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT,
              ApplicationSectionNameEnums.WEBINAR,
              ApplicationSectionNameEnums.ONLINE_COUNSELLING,
              ApplicationSectionNameEnums.TAKE_ADMISSION
            ];

            const configWithUpdatedVisit = sectionDetailsInfoTabsConfig.map(
              (section) => {
                if (
                  section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
                ) {
                  return {
                    ...section,
                    shouldShowQualifiedReportPreview: false,
                    shouldShowResultInProgressScreen: false,
                    subSection: section.subSection.map((sub) =>
                      sub.sectionId === targetSectionId
                        ? {
                            ...sub,
                            firstVisitedDatetime: event.firstVisitedDatetime
                          }
                        : sub
                    )
                  };
                }
                if (postNatSectionIds.includes(section.id)) {
                  return { ...section, isHidden: false, isDisable: false };
                }
                return section;
              }
            );

            const {
              sectionDetailsInfoTabsDetailsArray: recalculatedConfig,
              selectedSectionTabId,
              selectedSubSectionId,
              selectedSectionCompletionPercentage
            } = getSectionDetailsDisableInfoTabsConfig(
              configWithUpdatedVisit,
              context.loggedInUserRole,
              context.isRescheduleExam,
              context.userApplicationDetails?.applicationType,
              context.initialValues?.APPLICATION_TYPE,
              isDirectCampusVisitedApplication(context.initialValues)
            );

            let finalSectionTabId = selectedSectionTabId;
            let finalSubSectionId = selectedSubSectionId;
            let finalCompletionPercentage = selectedSectionCompletionPercentage;

            if (
              finalSectionTabId ===
                ApplicationSectionNameEnums.NAT_SLOT_SELECTION ||
              finalSectionTabId === ApplicationSectionNameEnums.NAT
            ) {
              const firstPostNatSection = recalculatedConfig.find(
                (s) =>
                  postNatSectionIds.includes(s.id) &&
                  !s.isHidden &&
                  !s.isDisable
              );
              if (firstPostNatSection) {
                finalSectionTabId = firstPostNatSection.id;
                finalCompletionPercentage =
                  firstPostNatSection.completionPercentage;
                finalSubSectionId =
                  firstPostNatSection.subSection.length > 0
                    ? firstPostNatSection.subSection[0].id
                    : '';
              }
            }

            return {
              sectionDetailsInfoTabsConfig: recalculatedConfig,
              currentSelectedSectionTabId: finalSectionTabId,
              currentSelectedSubSectionId: finalSubSectionId,
              currentSelectedSectionCompletionPercentage:
                finalCompletionPercentage
            };
          }
        ),

        [actions.onSuccessGetInitialValueOfAllFields]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const initialDetails = event?.data ?? {};
            const response = {
              UserIntermediateDetails: {
                ...initialDetails.user_intermediate_details?.success_response
              },
              UserProfile: {
                ...initialDetails.user_profile_details?.success_response
              },
              UserSSCDetails: {
                ...initialDetails.user_ssc_details?.success_response
              },
              UserGuardianDetails: {
                ...initialDetails.user_guardian_details?.success_response
              },
              UserAddressDetails: {
                ...(initialDetails.user_address_details?.success_response?.find(
                  (address) => address.address_type === 'PERMANENT'
                ) || {})
              },
              UserCurrentProfessionalDetails: {
                ...initialDetails.user_professional_details?.success_response
              }
            };

            const isUserProfileEmailVerified =
              response.UserProfile.is_email_verified;

            const fieldsGraphQlConfig = context.fieldsGraphQlConfig;

            const initialValues = {};

            Object.keys(fieldsGraphQlConfig).forEach((eachFieldName) => {
              const fieldGraphQlConfig = fieldsGraphQlConfig[eachFieldName];
              const fieldName = fieldGraphQlConfig.fieldName;
              const modelName = fieldGraphQlConfig.modelName;
              if (fieldName && modelName)
                initialValues[eachFieldName] =
                  response[modelName][fieldName] ?? '';
              else initialValues[eachFieldName] = '';
            });

            if (initialValues['PREFERRED_LANGUAGE'].length === 0) {
              initialValues['PREFERRED_LANGUAGE'] = '';
            }

            return {
              initialValues: initialValues,
              disableFieldValues: initialValues,
              isUserProfileEmailVerified: isUserProfileEmailVerified
            };
          }
        ),

        [actions.onSuccessGettingTemplateResponse]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const {
              fieldsTemplateResponseConfig,
              initialValues,
              sectionDetailsInfoTabsConfig,
              loggedInUserRole,
              userApplicationDetails
            } = context;
            const { field_responses } = event?.data;
            let templateApplicationType = initialValues?.APPLICATION_TYPE;

            Object.keys(fieldsTemplateResponseConfig).forEach((eachField) => {
              initialValues[
                fieldsTemplateResponseConfig[eachField].field_name
              ] = '';
            });

            if (field_responses && field_responses.length > 0) {
              field_responses.forEach((eachFieldResponse) => {
                const { field_id, field_value } = eachFieldResponse;

                if (
                  field_id === OFFLINE_EXAM_SUBMISSION_APPLICATION_TYPE_FIELD_ID
                ) {
                  templateApplicationType = field_value;
                }

                if (fieldsTemplateResponseConfig[field_id]) {
                  initialValues[
                    fieldsTemplateResponseConfig[field_id].field_name
                  ] = field_value;
                }
              });
            }

            const {
              sectionDetailsInfoTabsDetailsArray: recalculatedSectionConfig
            } = getSectionDetailsDisableInfoTabsConfig(
              sectionDetailsInfoTabsConfig,
              loggedInUserRole,
              context.isRescheduleExam,
              userApplicationDetails?.applicationType,
              templateApplicationType,
              isDirectCampusVisitedApplication(initialValues)
            );

            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getInitialValueOfAllFieldsAPIStatus: SUCCESS
              },
              disableFieldValues: initialValues,
              sectionDetailsInfoTabsConfig: recalculatedSectionConfig
            };
          }
        ),

        [actions.onErrorGettingTemplateResponse]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const errorEnum = getAPIErrorEnum(event?.data);

            if (errorEnum === 'APPLICATION_SECTION_ENTITY_RESPONSE_NOT_FOUND') {
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  getInitialValueOfAllFieldsAPIStatus: SUCCESS
                }
              };
            }
            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getInitialValueOfAllFieldsAPIStatus: ERROR
              }
            };
          }
        ),

        [actions.onFailGetInitialValueOfAllFields]: assign(
          (context: NiatApplicationMachineContextType) => {
            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getInitialValueOfAllFieldsAPIStatus: ERROR
              }
            };
          }
        ),

        [actions.onEntryUpdateFieldValues]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const {
              sectionDetailsInfoTabsConfig,
              currentSelectedSectionTabId,
              currentSelectedSubSectionId,
              currentSelectedSectionCompletionPercentage
            } = context;
            const currentSelectedSection = currentSelectedSubSectionId
              ? currentSelectedSubSectionId
              : currentSelectedSectionTabId;

            const { fieldDetail } = event as UpdateFieldValues;
            const fieldSectionId =
              context.fieldsGraphQlConfig?.[fieldDetail?.field]?.sectionId;
            let sectionToUpdate = currentSelectedSection;
            let sectionToUpdateCompletionPercentage =
              currentSelectedSectionCompletionPercentage;

            if (fieldSectionId != null) {
              for (const section of sectionDetailsInfoTabsConfig) {
                if (String(section.sectionId) === String(fieldSectionId)) {
                  sectionToUpdate = section.id;
                  sectionToUpdateCompletionPercentage =
                    section.completionPercentage ?? 0;
                  break;
                }

                const matchedSubSection = section.subSection?.find(
                  (subSection) =>
                    String(subSection.sectionId) === String(fieldSectionId)
                );

                if (matchedSubSection) {
                  sectionToUpdate = matchedSubSection.id;
                  sectionToUpdateCompletionPercentage =
                    matchedSubSection.completionPercentage ?? 0;
                  break;
                }
              }
            }
            let newFieldUpdateDetailsQueue = context.fieldUpdateDetailsQueue;
            newFieldUpdateDetailsQueue.push(fieldDetail);
            return {
              fieldUpdateDetailsQueue: newFieldUpdateDetailsQueue,
              currentSectionToUpdateField: sectionToUpdate,
              currentSectionToUpdateCompletionPercentage:
                sectionToUpdateCompletionPercentage
            };
          }
        ),

        [actions.onSuccessUpdateFieldValues]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const {
              sectionDetailsInfoTabsConfig,
              initialValues,
              values,
              currentSectionToUpdateCompletionPercentage,
              currentSectionToUpdateField,
              currentSelectedSectionTabId,
              currentSelectedSubSectionId
            } = context;

            const response = event.data;

            let completionPercentage = null;
            if (response?.completion_percentage) {
              completionPercentage = response.completion_percentage;
            } else if (response) {
              Object.keys(response).forEach((eachKey) => {
                if (response[eachKey]?.completion_percentage) {
                  const sectionCompletionPercentage =
                    response[eachKey].completion_percentage;
                  if (
                    completionPercentage &&
                    sectionCompletionPercentage > completionPercentage
                  ) {
                    completionPercentage = sectionCompletionPercentage;
                  } else if (completionPercentage === null) {
                    completionPercentage = sectionCompletionPercentage;
                  }
                }
              });
            }

            const currentSelectedSection = currentSectionToUpdateField;

            const newSectionDetailsInfoTabsConfig =
              completionPercentage &&
              sectionDetailsInfoTabsConfig.map((eachSection) => {
                if (eachSection.id === currentSelectedSection) {
                  return {
                    ...eachSection,
                    completionPercentage
                  };
                }

                if (eachSection.subSection?.length) {
                  const updatedSubSections = eachSection.subSection.map(
                    (subSection) => {
                      if (subSection.id === currentSelectedSection) {
                        return {
                          ...subSection,
                          completionPercentage
                        };
                      }
                      return subSection;
                    }
                  );
                  const totalSubSections = updatedSubSections.length;
                  const sumOfCompletionPercentages = updatedSubSections.reduce(
                    (sum, subSection) =>
                      sum + (subSection.completionPercentage || 0),
                    0
                  );

                  const updatedCompletionPercentage =
                    totalSubSections > 0
                      ? sumOfCompletionPercentages / totalSubSections
                      : eachSection.completionPercentage;

                  return {
                    ...eachSection,
                    subSection: updatedSubSections,
                    completionPercentage: updatedCompletionPercentage
                  };
                }

                return eachSection;
              });

            let combineValues = initialValues;

            const newFieldUpdateDetailsQueue = context.fieldUpdateDetailsQueue;
            if (newFieldUpdateDetailsQueue.length > 0) {
              const lastFieldUpdateDetail = newFieldUpdateDetailsQueue.shift();
              const { field, value } = lastFieldUpdateDetail;

              combineValues = {
                ...initialValues,
                [field]: value
              };
            }

            if (completionPercentage >= 100) {
              combineValues = {
                ...initialValues,
                ...values
              };
            }

            if (newSectionDetailsInfoTabsConfig) {
              if (
                completionPercentage >= 100 &&
                currentSectionToUpdateCompletionPercentage >= 100
              ) {
                return {
                  initialValues: combineValues,
                  fieldUpdateDetailsQueue: newFieldUpdateDetailsQueue
                };
              } else {
                const {
                  sectionDetailsInfoTabsDetailsArray:
                    newSectionDetailsDisableConfig
                } = getSectionDetailsDisableInfoTabsConfig(
                  newSectionDetailsInfoTabsConfig,
                  context.loggedInUserRole,
                  context.isRescheduleExam,
                  context.userApplicationDetails?.applicationType,
                  combineValues?.APPLICATION_TYPE,
                  isDirectCampusVisitedApplication(combineValues)
                );

                return {
                  sectionDetailsInfoTabsConfig: newSectionDetailsDisableConfig,
                  currentSelectedSectionCompletionPercentage:
                    completionPercentage,
                  initialValues: combineValues,
                  fieldUpdateDetailsQueue: newFieldUpdateDetailsQueue
                };
              }
            } else {
              const modelKey = Object.keys(response)[0];
              const failureResponse = response[modelKey].failure_response;
              const resStatus = failureResponse.res_status;
              if (resStatus === 'INVALID_USER_PROFILE_DETAILS') {
                const responseStr = failureResponse.response
                  .replace(/'/g, '"')
                  .replace(/None/g, 'null');
                const parsedResponse = JSON.parse(responseStr);
                const errorMessages = {};

                if (parsedResponse.profile_details) {
                  Object.entries(parsedResponse.profile_details).forEach(
                    ([key, value]) => {
                      if (
                        value &&
                        typeof value === 'object' &&
                        value.error_message
                      ) {
                        errorMessages[key] = value.error_message;
                      }
                    }
                  );
                }

                return {
                  updateFieldFailureErrorMessages: {
                    ...context.updateFieldFailureErrorMessages,
                    ...{
                      profile_details: errorMessages
                    }
                  }
                };
              }
              captureLogEntriesExceptionWithScope(
                LogEntriesConstantsEnums.GRAPHQL_FIELD_UPDATE_FAILURE,
                {
                  data: event.data
                },
                LogEntryTypesEnums.ERROR,
                LogEntryPriorityEnum.HIGH
              );

              return {};
            }
          }
        ),

        [actions.onFailUpdateFieldValues]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const newFieldUpdateDetailsQueue = context.fieldUpdateDetailsQueue;
          newFieldUpdateDetailsQueue.shift();
          captureLogEntriesExceptionWithScope(
            LogEntriesConstantsEnums.GRAPHQL_FIELD_UPDATE_API_FAILED,
            {
              event: event,
              currentSelectedSectionTabId: context.currentSelectedSectionTabId,
              currentSelectedSubSectionId: context.currentSelectedSubSectionId
            },
            LogEntryTypesEnums.ERROR,
            LogEntryPriorityEnum.HIGH
          );
          return {
            fieldUpdateDetailsQueue: newFieldUpdateDetailsQueue
          };
        },

        [actions.onSuccessGetStudentProfileDetail]: assign(
          (context, event: any) => {
            const studentProfileDetail = {
              userId: event.data.user_profile_details?.success_response?.user_id
            };

            return {
              studentProfileDetail: studentProfileDetail
            };
          }
        ),
        [actions.onSuccessGetParentProfileDetail]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const parentProfileDetail = {
              userId: event.data.user_profile_details?.success_response?.user_id
            };

            return {
              parentProfileDetail: parentProfileDetail
            };
          }
        ),
        [actions.onSuccessLogOutParent]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          setAccessTokenToDomain(context.studentLoginDetails?.accessToken);
          setRefreshTokenToDomain(context.studentLoginDetails?.refreshToken);
        },
        [actions.onSuccessGetFormConfig]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const sectionFormModel = new ApplicationSectionFormModel(
              event?.data
            );
            return {
              formSectionConfigModel: sectionFormModel
            };
          }
        ),

        [actions.onSuccessGetIbFormSubmissionDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              formSubmissionDetails: event.data,
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getIbFormSubmissionDetailsAPIStatus: SUCCESS
              }
            };
          }
        ),

        [actions.onSuccessGetBootcampRegistrationFormSubmission]: assign(
          (_context: NiatApplicationMachineContextType, event: any) => {
            return {
              bootcampRegistrationFormSubmissionDetails: event.data
            };
          }
        ),

        [actions.onErrorGetIbFormSubmissionDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const errorStatus = getAPIErrorEnum(event.data);

            if (errorStatus === 'NO_SUBMISSION_DETAILS_FOUND') {
              return {
                graphQlApiStatus: {
                  ...context.graphQlApiStatus,
                  getIbFormSubmissionDetailsAPIStatus: SUCCESS
                }
              };
            }

            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getIbFormSubmissionDetailsAPIStatus: ERROR
              }
            };
          }
        ),

        [actions.onSuccessSubmitApplicationForm]: assign(
          (context: NiatApplicationMachineContextType, event) => {
            const { errorMessages } = context;

            return {
              shouldShowSuccessPage: true,
              errorMessages: {
                ...errorMessages,
                applicationFormSubmitErrorMessage: ''
              }
            };
          }
        ),
        [actions.onSuccessGetStudentAuthDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              studentLoginDetails: {
                accessToken: event.data.access_token,
                refreshToken: event.data.refresh_token
              }
            };
          }
        ),

        [actions.onEntryGetPhoneNumberOtp]: (context) => {
          const now = Date.now();
          const last = lastOnEntryGetOtpLogTimeRef.current;
          if (last != null && now - last <= 2000) return;
          lastOnEntryGetOtpLogTimeRef.current = now;
          sendGAEventToDataLayer({
            event: 'parent_flow_student_number_get_otp'
          });
          const userId =
            (context as NiatApplicationMachineContextType)
              .userGraphQlProfileDetails?.user_id || '';
          const pageId =
            (context as NiatApplicationMachineContextType)
              .currentSelectedSubSectionId ||
            (context as NiatApplicationMachineContextType)
              .currentSelectedSectionTabId ||
            '';
          logEvent(
            CloudWatchEventNames.PARENT_FLOW_STUDENT_NUMBER_GET_OTP,
            'user_action',
            pageId,
            userId
          );
        },

        [actions.onSuccessPhoneNumberOtpVerification]: assign(
          (context, event) => {
            sendGAEventToDataLayer({
              event: 'parent_flow_student_number_verified'
            });
            const userId =
              (context as NiatApplicationMachineContextType)
                .userGraphQlProfileDetails?.user_id || '';
            const pageId =
              (context as NiatApplicationMachineContextType)
                .currentSelectedSubSectionId ||
              (context as NiatApplicationMachineContextType)
                .currentSelectedSectionTabId ||
              '';
            logEvent(
              CloudWatchEventNames.PARENT_FLOW_STUDENT_NUMBER_VERIFIED,
              'user_action',
              pageId,
              userId
            );

            return {
              fieldsAuthDetails: {
                ...context.fieldsAuthDetails,
                [context.currentAuthenticationField]: {
                  isOtpVerified: true
                }
              }
            };
          }
        ),
        [actions.onErrorUpdateSectionFieldValues]: (context, event: any) => {
          if (event.data?.status !== 'NO_MUTATION_PRESENT') {
            showErrorToast(
              `We're having some trouble completing your request. Please try again.`,
              {},
              null,
              {
                fontFamily: 'Inter',
                borderRadius: '12px',
                height: '100px'
              }
            );
          }
        },
        [actions.onSuccessRecoverStudentDetailsSync]: (
          _context: NiatApplicationMachineContextType,
          _event: any
        ) => {
          window.location.reload();
        },
        [actions.onErrorRecoverStudentDetailsSync]: (
          _context: NiatApplicationMachineContextType,
          _event: any
        ) => {
          showErrorToast(
            `We're having some trouble completing your request. Please try again.`,
            {},
            null,
            { fontFamily: 'Inter', borderRadius: '12px', height: '100px' }
          );
        },
        [actions.initializeProductsMachine]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const {
              sectionName,
              initialState,
              productCode: productCodeFromEvent
            } = event;

            const {
              applicationSectionDetailModel: {
                sectionsProductCodes,
                multiStepSectionConfig
              },
              userApplicationDetails: { applicationId }
            } = context;

            const productCode =
              productCodeFromEvent ?? sectionsProductCodes[sectionName];
            const sectionId = multiStepSectionConfig.find(
              (eachSection) => eachSection.sectionNameEnum === sectionName
            )?.sectionId;

            return {
              productsSectionMachineRefDetails: {
                ...context.productsSectionMachineRefDetails,
                [sectionName]: spawn(
                  getProductsMachineConfig(
                    productCode,
                    sectionName,
                    applicationId,
                    sectionId,
                    context.userApiInviteCode,
                    initialState
                  ),
                  {
                    sync: true
                  }
                )
              }
            };
          }
        ),
        [actions.onErrorSubmitApplicationForm]: assign(
          (context, event: any) => {
            const errorStatus = getAPIErrorEnum(event.data);

            if (
              errorStatus ===
              'ALLOWED_NUMBER_OF_FORM_SUBMISSIONS_COMPLETED_FOR_USER'
            ) {
              return {
                errorMessages: {
                  ...context.errorMessages,
                  applicationFormSubmitErrorMessage:
                    'You have completed allowed number of submissions for this form'
                }
              };
            } else
              return {
                errorMessages: {
                  ...context.errorMessages,
                  applicationFormSubmitErrorMessage: getAPIErrorResponse(
                    event.data
                  )
                }
              };
          }
        ),
        [actions.onSuccessGetApplicationDiscountDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const discountDetailsModel = new DiscountDetailsModel(event.data);

            if (
              discountDetailsModel.interactionState === null ||
              !discountDetailsModel.couponCode
            ) {
              return {
                discountDetails: null,
                isDiscountPopupVisible: false,
                isTimerDiscountPopupVisible: false,
                isCouponTimerBannerVisible: false,
                couponTimerBannerTextDetails: null,
                isGracePeriodPopupVisible: false
              };
            }

            const hasCouponCodeInQuery = context.couponCode !== '';
            const isFeeCompleted = context.sectionDetailsInfoTabsConfig?.some(
              (section: any) => {
                if (
                  section.id ===
                    ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE &&
                  section.completionPercentage >= 100
                ) {
                  return true;
                }
                return section.subSection?.some(
                  (subSection: any) =>
                    subSection.id ===
                      ApplicationSectionNameEnums.APPLICATION_FEE_PAYMENT &&
                    subSection.completionPercentage >= 100
                );
              }
            );

            const isPersonalDetailsCompleted =
              context.sectionDetailsInfoTabsConfig?.some(
                (section: any) =>
                  section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS &&
                  section.completionPercentage >= 100
              );

            // Check if NOT offline exam submission
            const isOfflineExamSubmission = isOfflineExamSubmissionApplication(
              context.userApplicationDetails?.applicationType,
              context.initialValues?.APPLICATION_TYPE
            );

            const isDirectCampusVisited = isDirectCampusVisitedApplication(
              context.initialValues
            );
            const isNoInteraction =
              discountDetailsModel.interactionState === 'NO_INTERACTION';
            const isCopied = discountDetailsModel.interactionState === 'COPIED';
            const isTimerExpired =
              discountDetailsModel.expiryDatetime &&
              discountDetailsModel.currentDatetime &&
              getTimerExpiryState(
                discountDetailsModel.expiryDatetime,
                discountDetailsModel.currentDatetime
              );

            const shouldShowOnlyDiscountPopup =
              isNoInteraction &&
              discountDetailsModel.expiryDatetime === null &&
              !hasCouponCodeInQuery &&
              !isPersonalDetailsCompleted &&
              !isFeeCompleted &&
              !isOfflineExamSubmission &&
              !isDirectCampusVisited;

            const shouldShowTimerPopup =
              isNoInteraction &&
              !hasCouponCodeInQuery &&
              !isFeeCompleted &&
              discountDetailsModel.expiryDatetime !== null &&
              !isTimerExpired &&
              !isOfflineExamSubmission &&
              !isDirectCampusVisited;

            const shouldShowCouponTimerBanner =
              isCopied &&
              !hasCouponCodeInQuery &&
              !isFeeCompleted &&
              discountDetailsModel.expiryDatetime !== null &&
              !isTimerExpired &&
              !isOfflineExamSubmission &&
              !isDirectCampusVisited;

            const shouldShowGracePeriodPopup =
              discountDetailsModel.isGracePeriodApplicable &&
              isTimerExpired &&
              !hasCouponCodeInQuery &&
              !isFeeCompleted &&
              discountDetailsModel.expiryDatetime !== null &&
              discountDetailsModel.gracePeriodMinutes > 0 &&
              !isOfflineExamSubmission &&
              !isDirectCampusVisited;

            return {
              discountDetails: discountDetailsModel,
              isDiscountPopupVisible: shouldShowOnlyDiscountPopup,
              isTimerDiscountPopupVisible: shouldShowTimerPopup,
              isCouponTimerBannerVisible: shouldShowCouponTimerBanner,
              isGracePeriodPopupVisible: shouldShowGracePeriodPopup
            };
          }
        ),
        [actions.onSuccessRecordDiscountInteraction]: assign(
          (context, event: any) => {
            const interactionType = event.data?.interaction_type;
            if (!context.discountDetails) {
              return {};
            }
            const updates: Record<string, any> = {
              discountDetails: {
                ...context.discountDetails,
                interactionState:
                  interactionType || context.discountDetails.interactionState
              }
            };
            if (interactionType === 'DISMISSED') {
              updates.isDiscountPopupVisible = false;
              updates.isGracePeriodPopupVisible = false;
              updates.isTimerDiscountPopupVisible = false;
              updates.isGracePeriod = false;
              updates.isCouponTimerBannerVisible = false;
              updates.couponTimerBannerTextDetails = null;
            } else if (
              (interactionType === 'COPIED' &&
                context.discountDetails?.expiryDatetime !== null) ||
              context.discountDetails?.isGracePeriodApplicable
            ) {
              updates.isCouponTimerBannerVisible = true;
              updates.isTimerDiscountPopupVisible = false;
              updates.isDiscountPopupVisible = false;
              updates.isGracePeriodPopupVisible = false;
            }
            return updates;
          }
        ),
        [actions.setUpdateCouponExpiryLoading]: assign(
          (context: NiatApplicationMachineContextType) => ({
            updateCouponExpiryApiStatus: LOADING,
            errorMessages: {
              ...context.errorMessages,
              updateCouponExpiryErrorMessage: ''
            }
          })
        ),
        [actions.onErrorUpdateCouponExpiry]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            updateCouponExpiryApiStatus: ERROR,
            errorMessages: {
              ...context.errorMessages,
              updateCouponExpiryErrorMessage:
                getAPIErrorResponse(event.data) ||
                'Something went wrong. Please try again.'
            }
          })
        ),
        [actions.onSuccessUpdateCouponExpiry]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            // event.data contains the response from updateCouponExpiryDatetime API
            const { expiry_datetime, current_datetime } = event.data;

            return {
              discountDetails: {
                ...context.discountDetails,
                isGracePeriodApplicable: false,
                interactionState: 'COPIED',
                expiryDatetime: expiry_datetime,
                currentDatetime: current_datetime
              },
              hasRecordedCopyInteraction: true,
              updateCouponExpiryApiStatus: SUCCESS,
              errorMessages: {
                ...context.errorMessages,
                updateCouponExpiryErrorMessage: ''
              },
              isCouponTimerBannerVisible: true,
              couponTimerBannerTextDetails: null,
              isGracePeriodPopupVisible: false,
              isTimerDiscountPopupVisible: false,
              isDiscountPopupVisible: false
            };
          }
        ),
        [actions.setDiscountPopupVisibility]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            isDiscountPopupVisible: event.isVisible
          })
        ),
        [actions.setHasRecordedCopyInteraction]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            hasRecordedCopyInteraction: event.hasRecorded
          })
        ),
        [actions.updateTimerBannerVisibility]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            isCouponTimerBannerVisible: event.isVisible,
            ...(event.isVisible ? {} : { couponTimerBannerTextDetails: null })
          })
        ),
        [actions.onCouponTimerExpire]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const { discountDetails } = context;
            const shouldShowGracePeriodPopup = Boolean(
              discountDetails?.isGracePeriodApplicable &&
                (discountDetails?.gracePeriodMinutes ?? 0) > 0
            );

            return {
              hasRecordedCopyInteraction: false,
              isCouponTimerBannerVisible: false,
              couponTimerBannerTextDetails: null,
              isDiscountPopupVisible: false,
              isTimerDiscountPopupVisible: false,
              isGracePeriodPopupVisible: shouldShowGracePeriodPopup,
              updateCouponExpiryApiStatus: IDLE,
              errorMessages: {
                ...context.errorMessages,
                updateCouponExpiryErrorMessage: ''
              }
            };
          }
        ),
        [actions.updateTextInBanner]: assign(
          (_context: NiatApplicationMachineContextType, event: any) => ({
            couponTimerBannerTextDetails: event.textDetails,
            isCouponTimerBannerVisible: true
          })
        ),
        [actions.logCloudWatchEvent]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            eventName,
            eventType = 'user_action',
            additionalData = {}
          } = event;
          const userId = context.userGraphQlProfileDetails?.user_id || '';
          const pageId =
            context.currentSelectedSubSectionId ||
            context.currentSelectedSectionTabId ||
            '';
          logEvent(eventName, eventType, pageId, userId, additionalData);
        },
        [actions.onSuccessGetFormReferralCode]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const apiResponse = event.data as GetFormReferralCodeResponseType;
            const referralCode = apiResponse?.referral_code ?? null;
            const status = apiResponse?.status ?? null;
            const isActiveStatus = status === 'ACTIVE';
            const inviteCode = isActiveStatus
              ? referralCode || context.inviteCode
              : context.inviteCode || referralCode;
            return {
              inviteCode,
              formReferralCodes: apiResponse as GetFormReferralCodeResponseType
            };
          }
        ),
        [actions.onErrorTrackFormReferralAttemptLogEntry]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const resStatus = getAPIErrorEnum(event?.data);
          const inviteCodeFromParam = getQueryParamValue(INVITE_CODE);
          captureLogEntriesExceptionWithScope(
            LogEntriesConstantsEnums.TRACK_REFERRAL_ATTEMPT_API_FAILED,
            {
              res_status: resStatus,
              requestObject: {
                form_id: context.applicationSectionDetailModel?.formId,
                existing_licence_code: context.formReferralCodes?.referral_code,
                attempted_licence_code: inviteCodeFromParam
              }
            },
            LogEntryTypesEnums.ERROR,
            LogEntryPriorityEnum.HIGH
          );
        },
        [actions.onSuccessUpdateFirstVisitDatetime]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const sectionId = event?.data?.sectionId;

            return {
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                updateFirstVisitDatetimeApiStatus: {
                  ...context.graphQlApiStatus.updateFirstVisitDatetimeApiStatus,
                  [sectionId]: SUCCESS
                }
              }
            };
          }
        ),
        [actions.setIsFieldUpdateInProgressToTrue]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            isFieldUpdateInProgress: true
          })
        ),
        [actions.setIsFieldUpdateInProgressToFalse]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            isFieldUpdateInProgress: false
          })
        ),
        [actions.onSuccessGetTopinExamAuthCode]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            const code = event?.data?.code;
            const topinExamRedirection = context.topinExamRedirection;
            const topinExamRedirectionUrl = topinExamRedirection.url;
            const topinExamRedirectionTarget =
              topinExamRedirection.windowTarget;
            const url = new URL(topinExamRedirectionUrl, window.location.href);
            url.searchParams.set('auth_code', code);
            window.open(
              url.toString(),
              topinExamRedirectionTarget,
              'noopener,noreferrer'
            );
            return {
              isGetTopinExamAuthCodeLoading: false,
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getTopinExamAuthCodeAPIStatus: SUCCESS
              }
            };
          }
        ),
        [actions.setIsGetTopinExamAuthCodeLoadingToFalse]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            isGetTopinExamAuthCodeLoading: false,
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getTopinExamAuthCodeAPIStatus: ERROR
            }
          })
        ),
        [actions.onErrorGetTopinExamAuthCode]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const topinExamRedirectionLink = context.topinExamRedirection.url;
          window.open(
            topinExamRedirectionLink,
            context.topinExamRedirection.windowTarget,
            'noopener,noreferrer'
          );
        },
        [actions.setAvailableProgramInstitutesLoading]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getAvailableProgramInstitutesApiStatus: 'loading'
            }
          })
        ),
        [actions.onSuccessGetAvailableProgramInstitutes]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            availableProgramInstitutesData: event.data ?? null,
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getAvailableProgramInstitutesApiStatus: 'success'
            }
          })
        ),
        [actions.onErrorGetAvailableProgramInstitutes]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getAvailableProgramInstitutesApiStatus: 'error'
            }
          })
        ),
        [actions.onSuccessCreateUserProgramInstitute]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              createUserProgramInstituteApiStatus: SUCCESS
            }
          })
        ),
        [actions.onErrorCreateUserProgramInstitute]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              createUserProgramInstituteApiStatus: ERROR
            }
          })
        ),
        [actions.setUserProgramInstituteCoursesLoading]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getUserProgramInstituteCoursesApiStatus: 'loading'
            }
          })
        ),
        [actions.onSuccessGetUserProgramInstituteCourses]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            latestUserProgramInstituteCourseRow:
              getLatestUserProgramInstituteCourseRow(event.data ?? null),
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getUserProgramInstituteCoursesApiStatus: 'success'
            }
          })
        ),
        [actions.onErrorGetUserProgramInstituteCourses]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getUserProgramInstituteCoursesApiStatus: 'error'
            }
          })
        ),
        [actions.setUserOrdersDetailsLoading]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getUserOrdersDetailsApiStatus: 'loading'
            }
          })
        ),
        [actions.onSuccessGetUserOrdersDetails]: assign(
          (context: NiatApplicationMachineContextType, event: any) => {
            return {
              userOrdersDetails: event?.data?.user_orders_details ?? null,
              graphQlApiStatus: {
                ...context.graphQlApiStatus,
                getUserOrdersDetailsApiStatus: 'success'
              }
            };
          }
        ),
        [actions.onErrorGetUserOrdersDetails]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getUserOrdersDetailsApiStatus: 'error'
            }
          })
        ),
        [actions.onSuccessGetSelectedInstituteCourses]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            selectedInstituteData: event.data ?? null,
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getSelectedInstituteCoursesApiStatus: 'success'
            }
          })
        ),
        [actions.onErrorGetSelectedInstituteCourses]: assign(
          (context: NiatApplicationMachineContextType) => ({
            graphQlApiStatus: {
              ...context.graphQlApiStatus,
              getSelectedInstituteCoursesApiStatus: 'error'
            }
          })
        ),
        [actions.setUserEnrollMentStage]: assign(
          (context: NiatApplicationMachineContextType, event: any) => ({
            userEnrollMentStage: event.userEnrollMentStage
          })
        ),
        [actions.updateSeatReservationInstituteSectionDetails]: assign(
          (
            context: NiatApplicationMachineContextType,
            event: UpdateSeatReservationInstituteSectionDetailsEvent
          ) => ({
            seatReservationInstituteSectionDetails: {
              preferredUniversity: event.values.preferredUniversity,
              accommodationMode: event.values.accommodationMode
            }
          })
        )
      },
      services: {
        [services.createUserProgramInstitute]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { preferredUniversity, accommodationMode, instituteCourseId } =
            event.values;
          return createUserProgramInstituteApi({
            program_id: NIAT_PROGRAM_ID,
            institute_id: preferredUniversity,
            accommodation_mode: accommodationMode,
            institute_course_id: instituteCourseId
          });
        },
        [services.getUserProgramInstituteCourses]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          return getUserProgramInstituteCoursesApi({
            program_id: NIAT_PROGRAM_ID
          });
        },
        [services.getUserOrdersDetails]: async (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const apiResponse = await getUserOrdersDetailsApi();

          return apiResponse;
        },
        [services.getSelectedInstituteCourses]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          return getProgramInstituteCourses({
            program_id: NIAT_PROGRAM_ID,
            institute_id:
              context.latestUserProgramInstituteCourseRow?.institute_id
          });
        },
        [services.logOutParent]: (context) => {
          const userId = context.userGraphQlProfileDetails?.user_id || '';
          if (userId) {
            trackSessionEnd(userId);
          }
          return userLogoutAPI();
        },
        [services.getUserApplicationDetails]: (context, event: any) => {
          const { loggedInUserRole, studentLoginDetails } = context;

          const selectedQueryParams: Record<string, any> =
            getSelectedQueryParamsObject([
              ...utmParameters,
              'custom_id',
              'agent_code',
              'invite_code',
              'affiliate_code',
              'program_batch',
              'pi',
              'pm'
            ]);

          selectedQueryParams.device_details = device_details;

          const stringifiedSelectedQueryParams =
            Object.keys(selectedQueryParams).length > 0
              ? JSON.stringify(selectedQueryParams)
              : undefined;

          const requestObject = {
            metadata: stringifiedSelectedQueryParams
          };

          if (loggedInUserRole === 'PARENT') {
            return getApplicationDetailsApi({
              applicationNameEnum: ApplicationSectionNameEnums.NIAT_APPLICATION,
              requestObject,
              access_token: studentLoginDetails.accessToken
            });
          } else
            return getApplicationDetailsApi({
              applicationNameEnum: ApplicationSectionNameEnums.NIAT_APPLICATION,
              requestObject
            });
        },
        [services.createUserApplication]: (context, event: any) => {
          const selectedQueryParams: Record<string, any> =
            getSelectedQueryParamsObject([
              ...utmParameters,
              'custom_id',
              'agent_code',
              'invite_code',
              'affiliate_code',
              'program_batch',
              'pi',
              'pm'
            ]);

          selectedQueryParams.device_details = device_details;

          const stringifiedSelectedQueryParams =
            Object.keys(selectedQueryParams).length > 0
              ? JSON.stringify(selectedQueryParams)
              : undefined;

          return createUserApplicationApi({
            application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
            identity: context.loggedInUserRole as any,
            metadata: stringifiedSelectedQueryParams
          });
        },

        [services.updateUserApplication]: (context, event: any) => {
          const { initialValues, userApplicationDetails } = context;
          const selectedQueryParams: Record<string, any> =
            getSelectedQueryParamsObject([
              ...utmParameters,
              'custom_id',
              'agent_code',
              'invite_code',
              'affiliate_code',
              'program_batch',
              'pi',
              'pm'
            ]);

          selectedQueryParams.device_details = device_details;

          const stringifiedSelectedQueryParams =
            Object.keys(selectedQueryParams).length > 0
              ? JSON.stringify(selectedQueryParams)
              : undefined;

          return updateUserApplicationApi({
            application_id: userApplicationDetails.applicationId,
            identity: initialValues['WHO_IS_FILLING'],
            metadata: stringifiedSelectedQueryParams
          });
        },

        [services.getUserProfileDetailGraphQL]: (context, event: any) => {
          const query = getNiatApplicationDynamicQuery(
            'getUserProfileDetailGraphQL',
            [
              getUserProfileDetailGraphQlQuery([], false),
              getUserInviteCodeGraphQlQuery([], false)
            ]
          );
          return getMultipleGraphQlQueriesResponse(query);
        },
        [services.updateUserProfileDetailsGraphQL]: (context, event: any) => {
          return updateUserProfileDetailsGraphQLApi(
            sanitizeEmojisFromPayload({
              ...event.values
            })
          );
        },
        [services.updateUserIntermediateDetailGraphQL]: (
          context,
          event: any
        ) => {
          return updateUserIntermediateDetailGraphQLApi(
            sanitizeEmojisFromPayload({
              ...event.values
            })
          );
        },
        [services.updateUserDegreeDetailsGraphQL]: (context, event: any) => {
          const { valuesToUpdate } = event;

          const requestObjectForMutation = {
            UserDegreeDetails: {
              ...valuesToUpdate
            }
          };

          const { mutation, data } = getNiatApplicationDynamicMutation(
            'UpdateSectionFieldsValue',
            sanitizeEmojisFromPayload(requestObjectForMutation)
          );

          return updateMultipleGraphQlMutations({
            mutation: mutation,
            data: sanitizeEmojisFromPayload(data)
          });
        },
        [services.linkUserApplication]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            userApplicationDetails,
            userGraphQlProfileDetails,
            studentProfileDetail,
            isUserApplicationNotExit,
            parentProfileDetail,
            loggedInUserRole
          } = context;

          const requestObject: Record<string, any> = {
            user_id: userGraphQlProfileDetails?.user_id ?? '',
            application_id: userApplicationDetails.applicationId,
            identity: IdentityEnums.Values.STUDENT
          };

          if (isUserApplicationNotExit) {
            requestObject.identity = IdentityEnums.Values.STUDENT;
            requestObject.user_id = studentProfileDetail['userId'];
          } else if (loggedInUserRole === 'PARENT') {
            requestObject.identity = IdentityEnums.Values.PARENT;
          }

          return linkUserApplicationApi(requestObject);
        },

        [services.linkStudentToParent]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { studentLoginDetails, values, userGraphQlProfileDetails } =
            context;

          const requestObject: any = {
            values: {
              country_code: '+91',
              phone_number: userGraphQlProfileDetails['phone_number']
            },
            accessToken: studentLoginDetails?.accessToken
          };

          if (
            context.currentAuthenticationField ===
            'PARENT_OR_GUARDIAN_MOBILE_NUMBER'
          ) {
            requestObject.values.phone_number =
              values['PARENT_OR_GUARDIAN_MOBILE_NUMBER'];
            requestObject.accessToken = undefined;
          } else {
            requestObject.values = {
              ...requestObject.values,
              first_name: values.userName,
              email: values.userEmail
            };
          }

          if (requestObject.values.phone_number === '') {
            return new Promise((resolve) => resolve({}));
          } else
            return updateUserGuardianDetailsGraphQLApi(
              sanitizeEmojisFromPayload(requestObject)
            );
        },
        [services.updateUserGuardianDetailsGraphQL]: (context, event: any) => {
          return updateUserGuardianDetailsGraphQLApi({
            values: sanitizeEmojisFromPayload(context.values)
          });
        },
        [services.getUserApplicationSectionsDetails]: () => {
          return getApplicationSectionsDetailsApi({
            applicationNameEnum: ApplicationSectionNameEnums.NIAT_APPLICATION
          });
        },
        [services.getInitialValueOfAllFields]: () => {
          const query = getNiatApplicationDynamicQuery(
            'getInitialValueOfAllFields',
            [
              getUserProfileDetailGraphQlQuery([], false),
              getUserIntermediateDetailsGraphQlQuery([], false),
              getUserGuardianDetailsGraphQlQuery([], false),
              getUserSSCDetailsGraphQlQuery([], false),
              getUserAddressDetailQuery([], false),
              getUserProfessionalDetailQuery([], false)
            ]
          );
          return getMultipleGraphQlQueriesResponse(query);
        },
        [services.updateFieldValues]: (context, event: any) => {
          let fieldDetails = event?.fieldDetail;
          if (!fieldDetails && event.data?.fieldDetail) {
            fieldDetails = event.data.fieldDetail;
          }
          return graphQlSingleFieldUpdate(context, fieldDetails);
        },
        [services.getStudentProfileDetailWithAccessToken]: (
          context: NiatApplicationMachineContextType,
          event
        ) => {
          return getUserProfileDetailGraphQLApi({
            keysToGetInSuccessResponse: ['user_id'],
            accessToken: context.studentLoginDetails.accessToken
          });
        },
        [services.getParentProfileDetailWithAccessToken]: (
          context: NiatApplicationMachineContextType,
          event
        ) => {
          return getUserProfileDetailGraphQLApi({
            keysToGetInSuccessResponse: ['user_id'],
            accessToken: context.parentLoginDetails.accessToken
          });
        },
        [services.updateStudentDetails]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { values } = context;

          const requestObjectForMutation: Record<string, any> = {};

          if (
            values.STUDENT_FULL_NAME ||
            values.STUDENT_GENDER ||
            values.STUDENT_EMAIL_ADDRESS ||
            values.STUDENT_PREFERRED_LANGUAGE ||
            values.STUDENT_FAMILY_INCOME
          ) {
            requestObjectForMutation.UserProfile = {
              name: values.STUDENT_FULL_NAME ?? '',
              gender: values.STUDENT_GENDER ?? '',
              email: values.STUDENT_EMAIL_ADDRESS ?? '',
              preferred_languages: values.STUDENT_PREFERRED_LANGUAGE ?? '',
              family_income: values.STUDENT_FAMILY_INCOME ?? ''
            };
          }

          if (values.STUDENT_NATIVE_STATE || values.STUDENT_NATIVE_DISTRICT) {
            requestObjectForMutation.UserAddressDetails = {
              state_name: values.STUDENT_NATIVE_STATE ?? '',
              district_name: values.STUDENT_NATIVE_DISTRICT ?? '',
              address_type: 'PERMANENT'
            };
          }

          if (
            values.STUDENT_CLASS_XII_COMPLETION_YEAR ||
            (values.STUDENT_ACADEMIC_STREAM &&
              values.STUDENT_CURRENT_EDUCATION != 'Degree')
          ) {
            requestObjectForMutation.UserIntermediateDetails = {
              year_of_completion:
                values.STUDENT_CLASS_XII_COMPLETION_YEAR ?? '',
              course_name:
                values.STUDENT_ACADEMIC_STREAM &&
                values.STUDENT_CURRENT_EDUCATION != 'Degree'
                  ? values.STUDENT_ACADEMIC_STREAM
                  : ''
            };
          }

          if (values.STUDENT_CLASS_X_COMPLETION_YEAR) {
            requestObjectForMutation.UserSSCDetails = {
              year_of_completion: values.STUDENT_CLASS_X_COMPLETION_YEAR ?? ''
            };
          }

          if (
            values.STUDENT_ACADEMIC_STREAM &&
            values.STUDENT_CURRENT_EDUCATION == 'Degree'
          ) {
            requestObjectForMutation.UserDegreeDetails = {
              course_name: values.STUDENT_ACADEMIC_STREAM ?? ''
            };
          }

          if (
            values.FULL_NAME ||
            values.MOBILE_NUMBER ||
            values.OCCUPATION_CATEGORY
          ) {
            requestObjectForMutation.UserGuardianDetails = {
              first_name: values.FULL_NAME ?? '',
              phone_number: values.MOBILE_NUMBER ?? '',
              job_role_name: values.OCCUPATION_CATEGORY ?? ''
            };
          }

          const { mutation, data } = getNiatApplicationDynamicMutation(
            'UpdateSectionFieldsValue',
            sanitizeEmojisFromPayload(requestObjectForMutation)
          );

          return updateMultipleGraphQlMutations({
            mutation: mutation,
            data: sanitizeEmojisFromPayload(data)
          });
        },

        [services.recoverStudentDetailsSync]: async (
          context: NiatApplicationMachineContextType,
          _event: any
        ) => {
          const {
            initialValues,
            userApplicationDetails,
            fieldsTemplateResponseConfig
          } = context;

          if (!initialValues) return {};

          const requestObjectForMutation: Record<string, any> = {};

          if (
            initialValues.STUDENT_FULL_NAME ||
            initialValues.STUDENT_GENDER ||
            initialValues.STUDENT_EMAIL_ADDRESS ||
            initialValues.STUDENT_PREFERRED_LANGUAGE ||
            initialValues.STUDENT_FAMILY_INCOME
          ) {
            requestObjectForMutation.UserProfile = {
              name: initialValues.STUDENT_FULL_NAME ?? '',
              gender: initialValues.STUDENT_GENDER ?? '',
              email: initialValues.STUDENT_EMAIL_ADDRESS ?? '',
              preferred_languages:
                initialValues.STUDENT_PREFERRED_LANGUAGE ?? '',
              family_income: initialValues.STUDENT_FAMILY_INCOME ?? ''
            };
          }

          if (
            initialValues.STUDENT_NATIVE_STATE ||
            initialValues.STUDENT_NATIVE_DISTRICT
          ) {
            requestObjectForMutation.UserAddressDetails = {
              state_name: initialValues.STUDENT_NATIVE_STATE ?? '',
              district_name: initialValues.STUDENT_NATIVE_DISTRICT ?? '',
              address_type: 'PERMANENT'
            };
          }

          if (
            initialValues.STUDENT_CLASS_XII_COMPLETION_YEAR ||
            (initialValues.STUDENT_ACADEMIC_STREAM &&
              initialValues.STUDENT_CURRENT_EDUCATION !== 'Degree')
          ) {
            requestObjectForMutation.UserIntermediateDetails = {
              year_of_completion:
                initialValues.STUDENT_CLASS_XII_COMPLETION_YEAR ?? '',
              course_name:
                initialValues.STUDENT_ACADEMIC_STREAM &&
                initialValues.STUDENT_CURRENT_EDUCATION !== 'Degree'
                  ? initialValues.STUDENT_ACADEMIC_STREAM
                  : ''
            };
          }

          if (initialValues.STUDENT_CLASS_X_COMPLETION_YEAR) {
            requestObjectForMutation.UserSSCDetails = {
              year_of_completion:
                initialValues.STUDENT_CLASS_X_COMPLETION_YEAR ?? ''
            };
          }

          if (
            initialValues.STUDENT_ACADEMIC_STREAM &&
            initialValues.STUDENT_CURRENT_EDUCATION === 'Degree'
          ) {
            requestObjectForMutation.UserDegreeDetails = {
              course_name: initialValues.STUDENT_ACADEMIC_STREAM ?? ''
            };
          }

          if (
            initialValues.FULL_NAME ||
            initialValues.MOBILE_NUMBER ||
            initialValues.OCCUPATION_CATEGORY
          ) {
            requestObjectForMutation.UserGuardianDetails = {
              first_name: initialValues.FULL_NAME ?? '',
              phone_number: initialValues.MOBILE_NUMBER ?? '',
              job_role_name: initialValues.OCCUPATION_CATEGORY ?? ''
            };
          }

          if (Object.keys(requestObjectForMutation).length > 0) {
            const { mutation, data } = getNiatApplicationDynamicMutation(
              'UpdateSectionFieldsValue',
              sanitizeEmojisFromPayload(requestObjectForMutation)
            );
            await updateMultipleGraphQlMutations({
              mutation,
              data: sanitizeEmojisFromPayload(data)
            });
          }

          const whoIsFillingRequest: UpdateMultipleTemplateResponseRequestObjectType =
            {
              application_id: userApplicationDetails!.applicationId,
              template_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.template_id,
              section_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.section_id,
              field_responses: [
                {
                  field_id:
                    fieldsTemplateResponseConfig['WHO_IS_FILLING']?.field_id,
                  field_value: 'STUDENT'
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['CURRENT_EDUCATION']?.field_id,
                  field_value: initialValues.STUDENT_CURRENT_EDUCATION ?? ''
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['ACADEMIC_STREAM']?.field_id,
                  field_value: initialValues.STUDENT_ACADEMIC_STREAM ?? ''
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['CLASS_XII_BOARD']?.field_id,
                  field_value: initialValues.STUDENT_CLASS_XII_BOARD ?? ''
                }
              ]
            };
          return updateMultipleTemplateResponseApi(
            sanitizeEmojisFromPayload(whoIsFillingRequest)
          );
        },

        [services.getFormConfig]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const selectedSectionName =
            context.currentSelectedSubSectionId ||
            context.currentSelectedSectionTabId;
          const selectedSectionFormId =
            context.applicationSectionDetailModel.sectionsFormIds?.[
              selectedSectionName
            ];
          const formId =
            selectedSectionFormId ||
            context.applicationSectionDetailModel.formId;
          return getIbFormDetailsAPI({
            form_id: formId
          });
        },

        [services.getIbFormSubmissionDetails]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const selectedSectionName =
            context.currentSelectedSubSectionId ||
            context.currentSelectedSectionTabId;
          const selectedSectionFormId =
            context.applicationSectionDetailModel.sectionsFormIds?.[
              selectedSectionName
            ];
          const formId =
            selectedSectionFormId ||
            context.applicationSectionDetailModel.formId;
          return getIbFormSubmissionDetailsAPI({
            form_id: formId
          });
        },

        [services.getBootcampRegistrationFormSubmission]: (
          context: NiatApplicationMachineContextType
        ) => {
          const formId =
            context.applicationSectionDetailModel.sectionsFormIds?.[
              ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026
            ] || context.applicationSectionDetailModel.formId;
          return getIbFormSubmissionDetailsAPI({
            form_id: formId
          });
        },
        [services.submitApplicationForm]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            formSectionConfigModel,
            values,
            applicationSectionDetailModel,
            formReferralCodes
          } = context;
          let formData = {};
          formSectionConfigModel.formsRequestObjectKeys.forEach((fieldInfo) => {
            const keyToSend = fieldInfo.field_name_in_request_object;
            const fieldName = fieldInfo.field;
            formData[keyToSend] = values[fieldName];
          });
          formData[INVITE_CODE] = context.inviteCode;
          const formattedRequestObject = generateCommonRequestProperties({
            values: formData
          });

          formattedRequestObject.device_details = device_details;

          const attemptedLicenceCode = getQueryParamValue(INVITE_CODE);
          const shouldAddAttemptedLicenceCode =
            formReferralCodes?.referral_code &&
            attemptedLicenceCode &&
            formReferralCodes.referral_code !== attemptedLicenceCode;

          if (shouldAddAttemptedLicenceCode) {
            formattedRequestObject.attempted_licence_code =
              attemptedLicenceCode;
          }
          const sanitizedRequestObject = sanitizeEmojisFromPayload({
            ...formattedRequestObject
          });
          const requestObject = {
            form_id:
              applicationSectionDetailModel.sectionsFormIds?.[
                context.currentSelectedSubSectionId ||
                  context.currentSelectedSectionTabId
              ] || applicationSectionDetailModel.formId,
            form_submission_data: JSON.stringify(sanitizedRequestObject),
            submission_action: formSubmissionActionType.createAsNew
          };

          return createIbFormSubmissionAPI(requestObject);
        },
        [services.getStudentAuthDetails]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { userApplicationDetails } = context;
          let userId = '';
          userApplicationDetails.userApplicationDetails.map(
            (eachApplication) => {
              if (eachApplication.identity === 'STUDENT') {
                userId = eachApplication.userId;
              }
            }
          );

          return getUserIdentityAccessTokenApi({
            user_id: userId,
            application_id: userApplicationDetails.applicationId
          });
        },

        [services.updateTemplateResponse]: async (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { userApplicationDetails, fieldsTemplateResponseConfig } =
            context;

          const {
            fieldDetail: { field, value }
          } = event;

          const requestObject: UpdateTemplateResponseRequestObjectType = {
            application_id: userApplicationDetails.applicationId,
            template_id: fieldsTemplateResponseConfig[field]?.template_id,
            section_id: fieldsTemplateResponseConfig[field]?.section_id,
            field_id: fieldsTemplateResponseConfig[field]?.field_id,
            field_value: value
          };

          const primaryResult = await updateTemplateResponseApi(
            sanitizeEmojisFromPayload(requestObject)
          );

          const mappedResponses = event.whoIsFillingMappedTemplateResponses as
            | Array<{ field_id: string; field_value: string }>
            | undefined;

          if (
            field === 'WHO_IS_FILLING' &&
            mappedResponses &&
            mappedResponses.length > 0
          ) {
            const multipleRequestObject: UpdateMultipleTemplateResponseRequestObjectType =
              {
                application_id: userApplicationDetails.applicationId,
                template_id: fieldsTemplateResponseConfig[field]?.template_id,
                section_id: fieldsTemplateResponseConfig[field]?.section_id,
                field_responses: mappedResponses
              };

            await updateMultipleTemplateResponseApi(
              sanitizeEmojisFromPayload(multipleRequestObject)
            );
          }

          const mappedGraphQlUpdates =
            event.whoIsFillingMappedGraphQlUpdates as
              | Array<{
                  field: string;
                  value: string;
                  extraDetailToUpdate?: Record<string, any>;
                }>
              | undefined;

          if (
            field === 'WHO_IS_FILLING' &&
            mappedGraphQlUpdates &&
            mappedGraphQlUpdates.length > 0
          ) {
            await Promise.all(
              mappedGraphQlUpdates.map((detail) =>
                graphQlSingleFieldUpdate(context, detail)
              )
            );
          }

          return primaryResult;
        },
        [services.updateEnrollmentPaidStatus]: (
          context: NiatApplicationMachineContextType
        ) => {
          const orderId = String(
            context.pendingEnrollmentPaidCrmPayload?.orderId ?? ''
          ).trim();
          const values =
            context.pendingEnrollmentPaidCrmPayload?.values ?? null;
          if (!orderId || values == null) {
            return Promise.resolve({});
          }
          return updateUserExtraInfoApi({
            orderId,
            values
          }).catch(() => Promise.resolve({}));
        },

        [services.updateMultipleTemplateResponse]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            requestObjectArrayForTemplateResponse,
            userApplicationDetails,
            fieldsTemplateResponseConfig
          } = context;

          const requestObject: UpdateMultipleTemplateResponseRequestObjectType =
            {
              application_id: userApplicationDetails.applicationId,
              template_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.template_id,
              section_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.section_id,
              field_responses: requestObjectArrayForTemplateResponse
            };

          return updateMultipleTemplateResponseApi(
            sanitizeEmojisFromPayload(requestObject)
          );
        },

        [services.updateDefaultWhoAmIField]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            userApplicationDetails,
            values,
            fieldsTemplateResponseConfig,
            initialValues,
            loggedInUserRole
          } = context;

          const isValuePresent = initialValues['WHO_IS_FILLING'];

          if (isValuePresent) {
            return new Promise((resolve) => resolve({}));
          } else {
            const requestObject: UpdateTemplateResponseRequestObjectType = {
              application_id: userApplicationDetails.applicationId,
              template_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.template_id,
              section_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.section_id,
              field_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.field_id,
              field_value: 'STUDENT'
            };
            return updateTemplateResponseApi(
              sanitizeEmojisFromPayload(requestObject)
            );
          }
        },

        [services.getApplicationDiscountDetails]: (
          context: NiatApplicationMachineContextType
        ) => {
          return getApplicationDiscountDetailsApi({
            application_id: context.userApplicationDetails.applicationId
          });
        },
        [services.getAvailableProgramInstitutes]: (
          _context: NiatApplicationMachineContextType
        ) => {
          return nwProductsExtensionsServiceInstance.getAvailableProgramInstituteCourses(
            { program_id: NIAT_PROGRAM_ID }
          );
        },

        [services.recordDiscountInteraction]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          return recordDiscountInteractionApi({
            application_id: context.userApplicationDetails.applicationId,
            interaction_type: event.interactionType,
            coupon_code: context.discountDetails?.couponCode ?? ''
          });
        },

        [services.updateCouponExpiry]: async (
          context: NiatApplicationMachineContextType
        ) => {
          const { discountDetails, userApplicationDetails } = context;

          if (discountDetails?.interactionState !== 'COPIED') {
            await recordDiscountInteractionApi({
              application_id: userApplicationDetails.applicationId,
              interaction_type: 'COPIED',
              coupon_code: discountDetails?.couponCode ?? ''
            });
          }

          return updateCouponExpiryDatetimeApi({
            application_id: userApplicationDetails.applicationId,
            coupon_code: discountDetails?.couponCode ?? ''
          });
        },

        [services.getFormReferralCode]: (
          context: NiatApplicationMachineContextType
        ) => {
          const formId = context.applicationSectionDetailModel?.formId;
          return getFormReferralCodeApi({ formId });
        },

        [services.trackFormReferralAttempt]: (
          context: NiatApplicationMachineContextType
        ) => {
          const inviteCodeFromParam = getQueryParamValue(INVITE_CODE);
          if (!inviteCodeFromParam) return Promise.resolve({});
          return formReferralTrackApi({
            form_id: context.applicationSectionDetailModel?.formId,
            existing_licence_code: context.formReferralCodes?.referral_code,
            attempted_licence_code: inviteCodeFromParam
          });
        },
        [services.updateFirstVisitDatetime]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const applicationId = context.userApplicationDetails?.applicationId;
          const sectionId = event?.sectionId;

          return getOrCreateApplicationSectionVisitApi({
            application_id: applicationId,
            section_id: sectionId
          }).then((response) => ({
            sectionId,
            response
          }));
        },

        [services.updateWhoIsFilling]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const {
            userApplicationDetails,
            values,
            fieldsTemplateResponseConfig,
            fieldsAuthMachineRefDetails
          } = context;

          const requestObject: UpdateMultipleTemplateResponseRequestObjectType =
            {
              application_id: userApplicationDetails.applicationId,
              template_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.template_id,
              section_id:
                fieldsTemplateResponseConfig['WHO_IS_FILLING']?.section_id,
              field_responses: [
                {
                  field_id:
                    fieldsTemplateResponseConfig['WHO_IS_FILLING']?.field_id,
                  field_value: 'STUDENT'
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['CURRENT_EDUCATION']?.field_id,
                  field_value: values.STUDENT_CURRENT_EDUCATION ?? ''
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['ACADEMIC_STREAM']?.field_id,
                  field_value: values.STUDENT_ACADEMIC_STREAM ?? ''
                },
                {
                  field_id:
                    fieldsTemplateResponseConfig['CLASS_XII_BOARD']?.field_id,
                  field_value: values.STUDENT_CLASS_XII_BOARD ?? ''
                }
              ]
            };
          return updateMultipleTemplateResponseApi(
            sanitizeEmojisFromPayload(requestObject)
          );
        },

        [services.getTemplateResponse]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const { templatesToGetInitialValues, userApplicationDetails } =
            context;

          if (templatesToGetInitialValues.length === 0) {
            return new Promise((resolve, reject) => {
              resolve({
                field_responses: []
              });
            });
          }
          return getTemplateResponseApi({
            application_id: userApplicationDetails?.applicationId,
            template_id: templatesToGetInitialValues[0]
          });
        },
        [services.updateSectionFieldsValue]: (context, event: any) => {
          const { requestObjectForMutation } = event;

          const { mutation, data } = getNiatApplicationDynamicMutation(
            'UpdateSectionFieldsValue',
            sanitizeEmojisFromPayload(requestObjectForMutation)
          );

          if (mutation === '' || !mutation || !data) {
            return new Promise((resolve, rejects) => {
              rejects({
                error: 'no mutation present',
                status: 'NO_MUTATION_PRESENT'
              });
            });
          }

          return updateMultipleGraphQlMutations({
            mutation: mutation,
            data: sanitizeEmojisFromPayload(data)
          });
        },
        [services.sendEventOnPhoneNumberVerified]: (context, event: any) => {
          const {
            values,
            loggedInUserRole,
            initialValues,
            userGraphQlProfileDetails
          } = context;

          const loggedInUser =
            initialValues['WHO_IS_FILLING'] ??
            values['WHO_IS_FILLING'] ??
            loggedInUserRole;

          const eventData = {
            user_id: userGraphQlProfileDetails['user_id'],
            utm_campaign: getQueryParamValue('utm_campaign') ?? '',
            utm_medium: getQueryParamValue('utm_medium') ?? '',
            utm_term: getQueryParamValue('utm_term') ?? '',
            invite_code: context.inviteCode ?? '',
            utm_content: getQueryParamValue('utm_content') ?? '',
            utm_source: getQueryParamValue('utm_source') ?? '',
            custom_id: getQueryParamValue('custom_id') ?? '',
            agent_code: getQueryParamValue('agent_code') ?? '',
            user_type: loggedInUser,
            program_batch: getQueryParamValue('program_batch'),
            application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
            pi: getQueryParamValue('pi'),
            pm: getQueryParamValue('pm'),
            device_details: device_details
          };

          return sendUserEventApi({
            integration_type: 'SEGMENT',
            activity_data: JSON.stringify(eventData),
            activity_id: 'ce559001-4d76-4134-9ba9-d61507693aab',
            activity_reference_id: userGraphQlProfileDetails['user_id']
          });
        },
        [services.sendEventOnApplicationSubmit]: (context, event: any) => {
          const {
            values,
            initialValues,
            formSubmissionDetails,
            userGraphQlProfileDetails,
            sectionDetailsInfoTabsConfig
          } = context;

          const formattedRequestObject = generateCommonRequestProperties({
            values
          });

          const combineValues = {
            ...initialValues,
            ...values
          };

          let eventId = formSubmissionDetails
            ? '70a72c05-8315-49e6-96af-cee800c58957'
            : 'df43a7fd-1289-4a6b-bec7-b7f07043072d';

          const natSlotSelectionSection = sectionDetailsInfoTabsConfig.find(
            (section) =>
              section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
          );

          if (
            natSlotSelectionSection?.subSection?.[0]?.id ===
            ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_DETAILS
          ) {
            eventId = formSubmissionDetails
              ? '25f443c2-3e9a-480e-a278-0c94f4964665'
              : 'b6e3a1bd-2b3d-426a-bec0-7e97ffb56e36';
          }

          const eventData = {
            user_id: userGraphQlProfileDetails['user_id'],
            utm_campaign: getQueryParamValue('utm_campaign') ?? '',
            utm_medium: getQueryParamValue('utm_medium') ?? '',
            utm_term: getQueryParamValue('utm_term') ?? '',
            invite_code: context.inviteCode ?? '',
            utm_content: getQueryParamValue('utm_content') ?? '',
            utm_source: getQueryParamValue('utm_source') ?? '',
            custom_id: getQueryParamValue('custom_id') ?? '',
            agent_code: getQueryParamValue('agent_code') ?? '',
            org_asses_id: formattedRequestObject['org_asses_id'],
            exam_slot_time:
              formattedRequestObject['selected_webinar_slot_datetime'],
            preferred_slot: combineValues['timeSlots'],
            segmentDateTime: formattedRequestObject['segmentDateTime'],
            highest_qualification: combineValues['CURRENT_EDUCATION'],
            exam_type: combineValues['exam_type'],
            user_preferred_date:
              formattedRequestObject['selectADateToBookASlot'] &&
              format(
                parseISO(formattedRequestObject['selectADateToBookASlot']),
                'do MMMM'
              ),
            program_batch: getQueryParamValue('program_batch'),
            application_name_enum: ApplicationSectionNameEnums.NIAT_APPLICATION,
            pi: getQueryParamValue('pi'),
            pm: getQueryParamValue('pm'),
            device_details: device_details
          };

          return sendUserEventApi({
            integration_type: 'SEGMENT',
            activity_data: JSON.stringify(eventData),
            activity_id: eventId,
            activity_reference_id: userGraphQlProfileDetails['user_id']
          });
        },
        [services.getTopinExamAuthCode]: (context, event: any) => {
          return getClientAuthCodeAPI({ client_id: 'topin' });
        }
      },
      guards: {
        [guards.isUserApplicationNotExists]: (context, event: any) => {
          const errorEnum = getAPIErrorEnum(event?.data);
          if (errorEnum === 'USER_APPLICATION_NOT_EXISTS') return true;
          else return false;
        },
        [guards.isUserApplicationNotExistsAndLoggedInUserRoleParent]: (
          context,
          event: any
        ) => {
          const errorEnum = getAPIErrorEnum(event?.data);
          if (
            errorEnum === 'USER_APPLICATION_NOT_EXISTS' &&
            context.loggedInUserRole === 'PARENT'
          )
            return true;
          else return false;
        },
        [guards.isLoggedInUserRoleParent]: (context, event: any) => {
          const { loggedInUserRole } = context;
          if (loggedInUserRole === 'PARENT') return true;
          else return false;
        },
        [guards.isUserEligibleForDiscount]: (context, event: any) => {
          const { userApplicationDetails, initialValues } = context;
          const applicationType = userApplicationDetails?.applicationType;
          const templateApplicationType = initialValues?.APPLICATION_TYPE;

          const isOfflineExam = isOfflineExamSubmissionApplication(
            applicationType,
            templateApplicationType
          );
          const isDirectCampus =
            isDirectCampusVisitedApplication(initialValues);
          const isOfflineApplicationType =
            applicationType === 'OFFLINE_EXAM_SUBMISSION';

          return !isOfflineExam && !isDirectCampus && !isOfflineApplicationType;
        },
        [guards.isStudentApplicationExist]: (context, event: any) => {
          return context.userApplicationDetails.isStudentApplicationExist;
        },
        [guards.isParentApplicationExits]: (context, event: any) => {
          return context.userApplicationDetails.isParentApplicationExist;
        },
        [guards.checkIfStudentApplicationExistInResponse]: (
          context,
          event: any
        ) => {
          const data = event.data;
          let isStudentApplicationExist = false;
          data.user_application_details?.map((eachApplication) => {
            if (eachApplication.identity === 'STUDENT')
              isStudentApplicationExist = true;
          });
          return isStudentApplicationExist;
        },
        [guards.checkIfParentLoggedInAndStudentApplicationExit]: (
          context,
          event: any
        ) => {
          const data = event.data;
          const { loggedInUserRole } = context;
          let isStudentApplicationExist = false;
          data.user_application_details?.map((eachApplication) => {
            if (eachApplication.identity === 'STUDENT')
              isStudentApplicationExist = true;
          });

          if (loggedInUserRole === 'PARENT' && isStudentApplicationExist) {
            return true;
          }
          return false;
        },
        [guards.checkIfParentLoggedInAndStudentApplicationNotExit]: (
          context,
          event: any
        ) => {
          const data = event.data;

          let isParentLoggedIn = false;
          const loggedInUserId = context.userGraphQlProfileDetails['user_id'];

          let isStudentApplicationExist = false;
          data.user_application_details?.map((eachApplication) => {
            if (eachApplication.identity === 'STUDENT')
              isStudentApplicationExist = true;
            if (
              eachApplication.user_id === loggedInUserId &&
              eachApplication.identity === 'PARENT'
            ) {
              isParentLoggedIn = true;
            }
          });

          if (isParentLoggedIn && !isStudentApplicationExist) {
            return true;
          }
          return false;
        },
        [guards.checkIfParentLoggedIn]: (context, event: any) => {
          const data = event.data;
          let isParentLoggedIn = false;
          const loggedInUserId = context.userGraphQlProfileDetails['user_id'];
          data.user_application_details?.map((eachApplication) => {
            if (
              eachApplication.user_id === loggedInUserId &&
              eachApplication.identity === 'PARENT'
            ) {
              isParentLoggedIn = true;
            }
          });
          return isParentLoggedIn;
        },
        [guards.isParentApplicationExitAndParentLoggedIn]: (
          context,
          event: any
        ) => {
          return (
            context.userApplicationDetails?.isParentApplicationExist &&
            context.loggedInUserRole === 'PARENT'
          );
        },
        //NOTE: Need to change guard naming for student
        [guards.isCurrentAuthenticationFieldParentOrGuardianMobileNumber]: (
          context,
          event: any
        ) => {
          return context.currentAuthenticationField === 'STUDENT_MOBILE_NUMBER';
        },
        [guards.isCurrentAuthenticationFieldParentOrGuardianMobileNumberAndOtpVerified]:
          (context, event: any) => {
            return (
              context.currentAuthenticationField === 'STUDENT_MOBILE_NUMBER' &&
              context.fieldsAuthDetails?.[context.currentAuthenticationField]
                ?.isOtpVerified
            );
          },
        [guards.isProductAmountToBePaidZero]: (context, event: any) => {
          const {
            applicationProductInfo: { productFeeAfterDiscount, productFee },
            graphQlApiStatus
          } = context;
          const amount =
            graphQlApiStatus.getCouponCodeDetailApiStatus === SUCCESS &&
            productFeeAfterDiscount !== undefined
              ? productFeeAfterDiscount
              : productFee;
          return amount === 0;
        },
        [guards.isGraphQLApiHasFailureResponse]: (context, event: any) => {
          const eventData = event.data;
          let isGraphQLApiHasFailureResponse = false;
          Object.keys(eventData).forEach((key) => {
            if (eventData[key]?.failure_response) {
              isGraphQLApiHasFailureResponse = true;
            }
          });
          return isGraphQLApiHasFailureResponse;
        },
        [guards.shouldTrackFormReferralAttempt]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const referralCodeFromBackend = event?.data?.referral_code;
          const inviteCodeFromQuery = context.inviteCode;
          return Boolean(
            referralCodeFromBackend &&
              inviteCodeFromQuery &&
              referralCodeFromBackend !== inviteCodeFromQuery
          );
        },
        [guards.doesUserProgramInstituteCourseRowHaveInstituteId]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          const latestRow = getLatestUserProgramInstituteCourseRow(
            event.data ?? null
          );
          return Boolean(latestRow?.institute_id);
        },
        [guards.doesSelectedInstituteHaveCourses]: (
          context: NiatApplicationMachineContextType,
          event: any
        ) => {
          return Boolean(event?.data?.institute?.courses?.length);
        },
        [guards.shouldGetAvailableProgramInstitutesAfterUserOrders]: (
          context: NiatApplicationMachineContextType
        ) => {
          return (
            context.currentSectionType ===
              ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026 &&
            (context.userEnrollMentStage === 'YET_TO_DECIDE' ||
              context.userEnrollMentStage === 'NO_COURSES_AVAILABLE') &&
            context.graphQlApiStatus.getAvailableProgramInstitutesApiStatus !==
              'loading'
          );
        }
      }
    });
    const [niatApplicationMachineState, niatApplicationMachineSend] =
      useMachine(niatApplicationMachineConfig);

    return {
      niatApplicationMachineState,
      niatApplicationMachineSend,
      apiStatus: {
        getApplicationDetailAPIStatus,
        getApplicationSectionsDetailsAPIStatus,
        createIbFormSubmissionAPIStatus
      },
      apiErrors: {}
    };
  };
